<template>
  <div class="admin-container">
    <div class="content-wrapper">
      <div class="admin-header">
        <h1 class="page-title">管理中心</h1>
        <p class="page-subtitle">系统管理与配置</p>
      </div>
      
      <!-- 加载状态 -->
      <div v-if="loading" class="text-center my-10">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <div class="mt-3">加载中...</div>
      </div>

      <!-- 错误状态 -->
      <div v-else-if="error" class="text-center my-10">
        <v-alert type="error">{{ error }}</v-alert>
      </div>

      <!-- 主要内容区域 -->
      <div v-else>
        <div class="management-tabs-container">
          <v-tabs
            v-model="currentTab"
            grow
            fixed-tabs
            color="primary"
            bg-color="transparent"
            class="management-tabs"
          >
            <v-tab value="overview" class="tab-item">
              <v-icon start size="18">mdi-view-dashboard</v-icon>
              <span class="tab-text">概览</span>
            </v-tab>
            <v-tab value="settings" class="tab-item">
              <v-icon start size="18">mdi-cog</v-icon>
              <span class="tab-text">系统设置项</span>
            </v-tab>
            <v-tab value="wheel" class="tab-item">
              <v-icon start size="18">mdi-ferris-wheel</v-icon>
              <span class="tab-text">活动管理</span>
            </v-tab>
          </v-tabs>
        </div>

        <v-window v-model="currentTab">
          <!-- 设置项 Tab - 需要管理员权限 -->
          <v-window-item value="settings">
            <!-- 权限检查 -->
            <div v-if="!isAdmin" class="text-center my-10">
              <v-alert type="warning">
                权限不足，需要管理员权限才能访问设置项
              </v-alert>
            </div>
            
            <!-- 管理员设置内容 -->
            <div v-else>
            <!-- 媒体服务与访问控制 -->
            <v-card class="admin-card-enhanced mb-4">
              <v-card-title class="text-center">
                <v-icon start color="primary">mdi-server-network</v-icon> 媒体服务与访问控制
              </v-card-title>
              <v-card-text>
                <div v-if="adminLoading" class="text-center my-4">
                  <v-progress-circular indeterminate size="small" color="primary"></v-progress-circular>
                  <span class="ml-2">加载管理员设置中...</span>
                </div>
                
                <div v-else-if="adminError" class="mb-4">
                  <v-alert type="error" density="compact">{{ adminError }}</v-alert>
                </div>
                
                <div v-else>
                  <div class="d-flex justify-space-between mb-3 align-center">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="orange-darken-2" class="mr-2">mdi-plex</v-icon>
                      <span>Plex 注册开放：</span>
                    </div>
                    <v-switch
                      v-model="adminSettings.plex_register"
                      color="success"
                      density="compact"
                      hide-details
                      @change="updatePlexRegister"
                    ></v-switch>
                  </div>
                  
                  <div class="d-flex justify-space-between mb-3 align-center">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="green-darken-2" class="mr-2">mdi-emby</v-icon>
                      <span>Emby 注册开放：</span>
                    </div>
                    <v-switch
                      v-model="adminSettings.emby_register"
                      color="success"
                      density="compact"
                      hide-details
                      @change="updateEmbyRegister"
                    ></v-switch>
                  </div>

                  <v-divider class="my-3"></v-divider>

                  <!-- NSFW 媒体库配置 -->
                  <div>
                    <div class="d-flex align-center mb-2">
                      <v-icon size="small" color="pink-darken-2" class="mr-2">mdi-lock-open-outline</v-icon>
                      <span>NSFW 媒体库：</span>
                    </div>
                    <v-combobox
                      v-model="adminSettings.nsfw_libs"
                      multiple
                      chips
                      closable-chips
                      density="compact"
                      variant="outlined"
                      hide-details
                      placeholder="输入媒体库名称并回车添加"
                      @change="updateNsfwLibs"
                    ></v-combobox>
                    <div class="text-caption text-grey mt-1">
                      配置属于限制级的媒体库名称，用户解锁 NSFW 权限后方可访问
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- 高级线路控制 -->
            <v-card class="admin-card-enhanced mb-4">
              <v-card-title class="text-center">
                <v-icon start color="purple-darken-2">mdi-crown</v-icon> 高级线路控制
              </v-card-title>
              <v-card-text>
                <div v-if="!adminLoading && !adminError">
                  <div class="d-flex justify-space-between mb-3 align-center">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="purple-darken-2" class="mr-2">mdi-crown</v-icon>
                      <span>高级线路开放：</span>
                    </div>
                    <v-switch
                      v-model="adminSettings.premium_free"
                      color="success"
                      density="compact"
                      hide-details
                      @change="updatePremiumFree"
                    ></v-switch>
                  </div>
                  
                  <div class="d-flex justify-space-between mb-3 align-center">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="purple-darken-2" class="mr-2">mdi-lock-open</v-icon>
                      <span>Premium 解锁开放：</span>
                    </div>
                    <v-switch
                      v-model="adminSettings.premium_unlock_enabled"
                      color="success"
                      density="compact"
                      hide-details
                      @change="updatePremiumUnlockEnabled"
                    ></v-switch>
                  </div>
                  
                  <!-- 免费高级线路选择 -->
                  <div v-if="adminSettings.premium_free" class="mb-3">
                    <div class="d-flex align-center mb-2">
                      <v-icon size="small" color="purple-darken-2" class="mr-2">mdi-server-network</v-icon>
                      <span>免费高级线路选择：</span>
                    </div>
                    <v-select
                      v-model="adminSettings.free_premium_lines"
                      :items="adminSettings.premium_lines"
                      multiple
                      chips
                      closable-chips
                      label="选择免费开放的高级线路"
                      density="compact"
                      variant="outlined"
                      @update:model-value="updateFreePremiumLines"
                    >
                      <template v-slot:selection="{ item, index }">
                        <v-chip
                          v-if="index < 2"
                          size="small"
                          color="purple-lighten-3"
                          closable
                          @click:close="removeFreeLine(item.value)"
                        >
                          {{ item.title }}
                        </v-chip>
                        <span
                          v-if="index === 2"
                          class="text-grey text-caption align-self-center"
                        >
                          (+{{ adminSettings.free_premium_lines.length - 2 }} others)
                        </span>
                      </template>
                    </v-select>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- 捐赠管理 -->
            <v-card class="admin-card-enhanced mb-4">
              <v-card-title class="text-center">
                <v-icon start color="red-darken-2">mdi-gift</v-icon> 捐赠管理
              </v-card-title>
              <v-card-text>
                <div v-if="!adminLoading && !adminError">
                  <!-- 捐赠积分倍率 -->
                  <div class="d-flex justify-space-between align-center mb-3">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="red-darken-2" class="mr-2">mdi-cash-multiple</v-icon>
                      <span>捐赠积分倍率：</span>
                    </div>
                    <div class="d-flex align-center">
                      <v-text-field
                        v-model.number="adminSettings.donation_multiplier"
                        type="number"
                        density="compact"
                        variant="outlined"
                        hide-details
                        suffix="积分/元"
                        style="width: 140px"
                        min="0"
                        @blur="updateDonationMultiplier"
                        @keyup.enter="updateDonationMultiplier"
                      ></v-text-field>
                    </div>
                  </div>

                  <!-- UPAY 加密货币类型 -->
                  <div class="mb-3">
                    <div class="d-flex align-center mb-2">
                      <v-icon size="small" color="purple-darken-2" class="mr-2">mdi-bitcoin</v-icon>
                      <span>UPAY 支持加密货币类型：</span>
                    </div>
                    <v-combobox
                      v-model="adminSettings.upay_crypto_types"
                      multiple
                      chips
                      closable-chips
                      density="compact"
                      variant="outlined"
                      hide-details
                      placeholder="输入币种并回车添加（如 USDT-TRC20）"
                      @change="updateUpayCryptoTypes"
                    ></v-combobox>
                  </div>

                  <v-divider class="my-3"></v-divider>

                  <div class="donation-button-group">
                    <!-- 添加捐赠按钮 -->
                    <v-btn
                      color="red-darken-2"
                      variant="flat"
                      prepend-icon="mdi-plus"
                      @click="openDonationDialog"
                      class="donation-btn"
                      elevation="2"
                    >
                      添加捐赠
                    </v-btn>
                    
                    <!-- 登记管理按钮 -->
                    <v-btn
                      color="orange-darken-2"
                      variant="flat"
                      prepend-icon="mdi-clipboard-list"
                      @click="openDonationRegistrationDialog"
                      class="donation-btn"
                      elevation="2"
                    >
                      管理登记
                    </v-btn>
                    
                    <!-- Crypto 捐赠记录按钮 -->
                    <v-btn
                      color="purple-darken-2"
                      variant="flat"
                      prepend-icon="mdi-bitcoin"
                      @click="openCryptoDonationDialog"
                      class="donation-btn"
                      elevation="2"
                    >
                      查看记录
                    </v-btn>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- 系统管理 -->
            <v-card class="admin-card-enhanced mb-4">
              <v-card-title class="text-center">
                <v-icon start color="blue-darken-2">mdi-cogs</v-icon> 系统管理
              </v-card-title>
              <v-card-text>
                <div v-if="!adminLoading && !adminError">
                  <!-- 邀请码管理 -->
                  <div class="d-flex justify-space-between align-center mb-3">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="blue-darken-2" class="mr-2">mdi-ticket-confirmation</v-icon>
                      <span>邀请码管理：</span>
                    </div>
                    <v-btn
                      color="blue-darken-2"
                      variant="outlined"
                      size="small"
                      @click="openInviteCodeDialog"
                    >
                      <v-icon start size="small">mdi-plus</v-icon>
                      生成邀请码
                    </v-btn>
                  </div>
                  
                  <!-- 线路管理（包含标签管理） -->
                  <v-divider class="my-3"></v-divider>
                  <div class="d-flex justify-space-between align-center mb-3">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="green-darken-2" class="mr-2">mdi-server-network</v-icon>
                      <span>线路管理：</span>
                    </div>
                    <v-btn
                      color="green-darken-2"
                      variant="outlined"
                      size="small"
                      @click="openLineManagementDialog"
                    >
                      <v-icon start size="small">mdi-cog</v-icon>
                      管理线路
                    </v-btn>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- 自定义线路管理 -->
            <CustomLineManagement />

            <!-- 积分与额度设置 -->
            <v-card class="admin-card-enhanced mb-4">
              <v-card-title class="text-center">
                <v-icon start color="yellow-darken-2">mdi-star</v-icon> 积分与额度设置
              </v-card-title>
              <v-card-text>
                <div v-if="!adminLoading && !adminError">
                  <!-- 功能解锁积分 -->
                  <div class="text-caption font-weight-bold text-grey-darken-1 mb-2">功能解锁积分</div>

                  <!-- 解锁Premium每日积分设置 -->
                  <div class="d-flex justify-space-between align-center mb-3">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="amber-darken-2" class="mr-2">mdi-crown</v-icon>
                      <span>解锁 Premium 每日积分：</span>
                    </div>
                    <div class="d-flex align-center">
                      <v-text-field
                        v-model.number="adminSettings.premium_daily_credits"
                        type="number"
                        density="compact"
                        variant="outlined"
                        hide-details
                        style="width: 100px"
                        min="0"
                        max="10000"
                        @blur="updatePremiumDailyCredits"
                        @keyup.enter="updatePremiumDailyCredits"
                      ></v-text-field>
                    </div>
                  </div>

                  <!-- 解锁线路调度功能积分设置 -->
                  <div class="d-flex justify-space-between align-center mb-3">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="teal-darken-1" class="mr-2">mdi-calendar-clock</v-icon>
                      <span>解锁线路调度所需积分：</span>
                    </div>
                    <div class="d-flex align-center">
                      <v-text-field
                        v-model.number="adminSettings.line_schedule_unlock_credits"
                        type="number"
                        density="compact"
                        variant="outlined"
                        hide-details
                        style="width: 100px"
                        min="0"
                        max="10000"
                        @blur="updateLineScheduleUnlockCredits"
                        @keyup.enter="updateLineScheduleUnlockCredits"
                      ></v-text-field>
                    </div>
                  </div>
                  
                  <!-- 解锁下载/同步功能积分设置 -->
                  <div class="d-flex justify-space-between align-center mb-3">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="deep-purple-darken-1" class="mr-2">mdi-download</v-icon>
                      <span>解锁下载/同步所需积分：</span>
                    </div>
                    <div class="d-flex align-center">
                      <v-text-field
                        v-model.number="adminSettings.download_unlock_credits"
                        type="number"
                        density="compact"
                        variant="outlined"
                        hide-details
                        style="width: 100px"
                        min="0"
                        max="10000"
                        @blur="updateDownloadUnlockCredits"
                        @keyup.enter="updateDownloadUnlockCredits"
                      ></v-text-field>
                    </div>
                  </div>

                  <!-- 解锁NSFW积分设置 -->
                  <div class="d-flex justify-space-between align-center mb-3">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="grey-darken-1" class="mr-2">mdi-lock-open</v-icon>
                      <span>解锁 NSFW 所需积分：</span>
                    </div>
                    <div class="d-flex align-center">
                      <v-text-field
                        v-model.number="adminSettings.unlock_credits"
                        type="number"
                        density="compact"
                        variant="outlined"
                        hide-details
                        style="width: 100px"
                        min="0"
                        max="10000"
                        @blur="updateUnlockCredits"
                        @keyup.enter="updateUnlockCredits"
                      ></v-text-field>
                    </div>
                  </div>

                  <v-divider class="my-3"></v-divider>

                  <!-- 流量额度与超额扣费 -->
                  <div class="text-caption font-weight-bold text-grey-darken-1 mb-2">流量额度与超额扣费</div>

                  <!-- 普通用户每日免费 Premium 流量额度 -->
                  <div class="d-flex justify-space-between align-center mb-3">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="cyan-darken-2" class="mr-2">mdi-gauge</v-icon>
                      <span>普通用户每日免费流量：</span>
                    </div>
                    <div class="d-flex align-center">
                      <v-text-field
                        v-model.number="adminSettings.user_traffic_limit_gb"
                        type="number"
                        density="compact"
                        variant="outlined"
                        hide-details
                        suffix="GB"
                        style="width: 120px"
                        min="0"
                        step="0.1"
                        @blur="updateUserTrafficLimit"
                        @keyup.enter="updateUserTrafficLimit"
                      ></v-text-field>
                    </div>
                  </div>

                  <!-- 高级用户每日免费 Premium 流量额度 -->
                  <div class="d-flex justify-space-between align-center mb-3">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="purple-darken-2" class="mr-2">mdi-speedometer</v-icon>
                      <span>Premium 用户每日免费流量：</span>
                    </div>
                    <div class="d-flex align-center">
                      <v-text-field
                        v-model.number="adminSettings.premium_user_traffic_limit_gb"
                        type="number"
                        density="compact"
                        variant="outlined"
                        hide-details
                        suffix="GB"
                        style="width: 120px"
                        min="0"
                        step="0.1"
                        @blur="updatePremiumUserTrafficLimit"
                        @keyup.enter="updatePremiumUserTrafficLimit"
                      ></v-text-field>
                    </div>
                  </div>

                  <!-- 每 10GB 超额流量扣费积分 -->
                  <div class="d-flex justify-space-between align-center mb-3">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="blue-grey-darken-1" class="mr-2">mdi-database-minus</v-icon>
                      <span>每 10GB 超额流量扣费：</span>
                    </div>
                    <div class="d-flex align-center">
                      <v-text-field
                        v-model.number="adminSettings.credits_cost_per_10gb"
                        type="number"
                        density="compact"
                        variant="outlined"
                        hide-details
                        suffix="积分"
                        style="width: 120px"
                        min="0"
                        @blur="updateCreditsCostPer10Gb"
                        @keyup.enter="updateCreditsCostPer10Gb"
                      ></v-text-field>
                    </div>
                  </div>

                  <v-divider class="my-3"></v-divider>

                  <!-- 基础设置与转账 -->
                  <div class="text-caption font-weight-bold text-grey-darken-1 mb-2">基础设置与转账</div>

                  <!-- 邀请码积分设置 -->
                  <div class="d-flex justify-space-between align-center mb-3">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="grey-darken-1" class="mr-2">mdi-ticket-confirmation</v-icon>
                      <span>生成邀请码所需积分：</span>
                    </div>
                    <div class="d-flex align-center">
                      <v-text-field
                        v-model.number="adminSettings.invitation_credits"
                        type="number"
                        density="compact"
                        variant="outlined"
                        hide-details
                        style="width: 100px"
                        min="0"
                        max="10000"
                        @blur="updateInvitationCredits"
                        @keyup.enter="updateInvitationCredits"
                      ></v-text-field>
                    </div>
                  </div>

                  <!-- 积分转移功能开关 -->
                  <div class="d-flex justify-space-between align-center">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="blue-darken-1" class="mr-2">mdi-bank-transfer</v-icon>
                      <span>积分转移功能：</span>
                    </div>
                    <v-switch
                      v-model="adminSettings.credits_transfer_enabled"
                      color="success"
                      density="compact"
                      hide-details
                      @change="updateCreditsTransferEnabled"
                    ></v-switch>
                  </div>
                </div>
              </v-card-text>
            </v-card>

            <!-- Vaultwarden 兑换服务设置 -->
            <v-card class="admin-card-enhanced mb-4">
              <v-card-title class="text-center">
                <v-icon start color="blue-grey-darken-1">mdi-shield-key</v-icon> Vaultwarden 兑换设置
              </v-card-title>
              <v-card-text>
                <div v-if="!adminLoading && !adminError">
                  <!-- Vaultwarden 兑换开放开关 -->
                  <div class="d-flex justify-space-between align-center mb-3">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="blue-grey-darken-1" class="mr-2">mdi-lock-check</v-icon>
                      <span>Vaultwarden 兑换开放：</span>
                    </div>
                    <v-switch
                      v-model="adminSettings.vaultwarden_enabled"
                      color="success"
                      density="compact"
                      hide-details
                      @change="updateVaultwardenEnabled"
                    ></v-switch>
                  </div>

                  <!-- 账号兑换所需积分 -->
                  <div class="d-flex justify-space-between align-center">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="amber-darken-3" class="mr-2">mdi-star-circle</v-icon>
                      <span>账号兑换所需积分：</span>
                    </div>
                    <div class="d-flex align-center">
                      <v-text-field
                        v-model.number="adminSettings.vaultwarden_redeem_credits"
                        type="number"
                        density="compact"
                        variant="outlined"
                        hide-details
                        suffix="积分"
                        style="width: 120px"
                        min="0"
                        @blur="updateVaultwardenRedeemCredits"
                        @keyup.enter="updateVaultwardenRedeemCredits"
                      ></v-text-field>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>

             <!-- 勋章管理 -->
            <v-card class="admin-card-enhanced mb-4">
              <v-card-title class="text-center">
                <v-icon start color="amber-darken-2">mdi-medal</v-icon> 勋章管理
              </v-card-title>
              <v-card-text>
                <div v-if="badgesLoading" class="text-center my-4">
                  <v-progress-circular indeterminate size="small" color="primary"></v-progress-circular>
                  <span class="ml-2">加载勋章数据中...</span>
                </div>
                
                <div v-else-if="badgesError" class="mb-4">
                  <v-alert type="error" density="compact">{{ badgesError }}</v-alert>
                </div>
                
                <div v-else>
                  <!-- 勋章中心功能开关 -->
                  <div class="d-flex justify-space-between align-center mb-3">
                    <div class="d-flex align-center">
                      <v-icon size="small" color="amber-darken-2" class="mr-2">mdi-toggle-switch</v-icon>
                      <span>勋章中心开放：</span>
                    </div>
                    <v-switch
                      v-model="badgeConfig.enabled"
                      color="success"
                      density="compact"
                      hide-details
                      @change="updateBadgeConfig"
                    ></v-switch>
                  </div>
                  
                  <!-- 勋章中心提示消息 -->
                  <div v-if="badgeConfig.enabled" class="mb-3">
                    <div class="d-flex align-center mb-2">
                      <v-icon size="small" color="amber-darken-2" class="mr-2">mdi-message-text</v-icon>
                      <span>勋章中心提示消息：</span>
                    </div>
                    <v-textarea
                      v-model="badgeConfig.message"
                      density="compact"
                      variant="outlined"
                      rows="2"
                      placeholder="在勋章中心顶部显示的提示消息"
                      hide-details
                      @blur="updateBadgeConfig"
                    ></v-textarea>
                  </div>
                  
                  <v-divider class="my-3"></v-divider>
                  
                  <!-- 勋章列表 -->
                  <div class="mb-3">
                    <div class="d-flex justify-space-between align-center mb-2">
                      <div class="d-flex align-center">
                        <v-icon size="small" color="amber-darken-2" class="mr-2">mdi-medal-outline</v-icon>
                        <span>已创建的勋章：</span>
                      </div>
                      <v-btn
                        color="amber-darken-2"
                        variant="outlined"
                        size="small"
                        @click="openBadgeCreateDialog"
                      >
                        <v-icon start size="small">mdi-plus</v-icon>
                        创建勋章
                      </v-btn>
                    </div>
                    
                    <!-- 勋章列表 -->
                    <v-list v-if="badges.length > 0" class="badge-list">
                      <v-list-item
                        v-for="badge in badges"
                        :key="badge.id"
                        class="badge-list-item"
                        rounded="lg"
                      >
                        <template v-slot:prepend>
                          <v-avatar size="40">
                            <v-img :src="badge.icon_url" :alt="badge.name" />
                          </v-avatar>
                        </template>
                        
                        <v-list-item-title>
                          {{ badge.name }}
                          <v-chip
                            v-if="!badge.is_enabled"
                            size="x-small"
                            color="grey"
                            variant="flat"
                            class="ml-2"
                          >
                            已禁用
                          </v-chip>
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          成本: {{ badge.credits_cost }} 积分 | 加成: +{{ (badge.bonus_percentage * 100).toFixed(0) }}% | 有效期: {{ badge.valid_days }} 天
                        </v-list-item-subtitle>
                        
                        <template v-slot:append>
                          <v-btn
                            icon
                            size="small"
                            variant="text"
                            @click="editBadge(badge)"
                          >
                            <v-icon size="small">mdi-pencil</v-icon>
                          </v-btn>
                          <v-btn
                            icon
                            size="small"
                            variant="text"
                            color="error"
                            @click="deleteBadge(badge)"
                          >
                            <v-icon size="small">mdi-delete</v-icon>
                          </v-btn>
                        </template>
                      </v-list-item>
                    </v-list>
                    
                    <div v-else class="text-center text-medium-emphasis py-4">
                      <v-icon size="48" color="grey-lighten-1">mdi-medal-outline</v-icon>
                      <p class="mt-2">暂无勋章，点击上方按钮创建</p>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
            </div>
          </v-window-item>

          <!-- 活动管理 Tab - 需要管理员权限 -->
          <v-window-item value="wheel">
            <!-- 权限检查 -->
            <div v-if="!isAdmin" class="text-center my-10">
              <v-alert type="warning">
                权限不足，需要管理员权限才能访问活动管理
              </v-alert>
            </div>
            
            <!-- 活动管理内容 -->
            <div v-else class="activities-grid">
              <!-- 幸运大转盘活动卡片 -->
              <v-card class="activity-card-enhanced">
                <v-card-title class="d-flex align-center">
                  <v-icon class="mr-2" color="purple-darken-2">mdi-ferris-wheel</v-icon>
                  <span>幸运大转盘</span>
                  <v-spacer></v-spacer>
                  <v-chip color="success" size="small" variant="flat">
                    <v-icon start size="12">mdi-check-circle</v-icon>
                    运行中
                  </v-chip>
                </v-card-title>
                
                <v-card-text>
                  <p class="text-body-2 text-medium-emphasis mb-4">
                    管理转盘奖品配置、概率设置和随机性参数，查看抽奖统计数据
                  </p>
                  
                  <div class="activity-stats mb-4">
                    <v-row dense>
                      <v-col cols="6">
                        <div class="stat-item">
                          <div class="stat-value">{{ wheelStats.totalSpins || 0 }}</div>
                          <div class="stat-label">总抽奖次数</div>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="stat-item">
                          <div class="stat-value">{{ wheelStats.activeUsers || 0 }}</div>
                          <div class="stat-label">参与用户</div>
                        </div>
                      </v-col>
                    </v-row>
                    <v-row dense class="mt-2">
                      <v-col cols="6">
                        <div class="stat-item">
                          <div class="stat-value text-success">{{ wheelStats.todaySpins || 0 }}</div>
                          <div class="stat-label">今日抽奖</div>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="stat-item">
                          <div class="stat-value text-info">{{ wheelStats.lastWeekSpins || 0 }}</div>
                          <div class="stat-label">本周抽奖</div>
                        </div>
                      </v-col>
                    </v-row>
                    
                    <v-row dense>
                      <v-col cols="4">
                        <div class="stat-item">
                          <div class="stat-value text-warning">{{ wheelStats.totalCreditsChange?.toFixed(2) || '0.00' }}</div>
                          <div class="stat-label">总积分变化</div>
                        </div>
                      </v-col>
                      <v-col cols="4">
                        <div class="stat-item">
                          <div class="stat-value text-error">-{{ wheelStats.totalCostCredits || 0 }}</div>
                          <div class="stat-label">总消耗积分</div>
                        </div>
                      </v-col>
                      <v-col cols="4">
                        <div class="stat-item">
                          <div class="stat-value text-secondary">{{ wheelStats.totalInviteCodes || 0 }}</div>
                          <div class="stat-label">总邀请码发放</div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </v-card-text>
                
                <v-card-actions class="pa-4 pt-0">
                  <v-btn
                    color="purple-darken-2"
                    variant="elevated"
                    block
                    @click="openWheelManagement"
                  >
                    <v-icon start>mdi-cog</v-icon>
                    进入转盘管理
                  </v-btn>
                </v-card-actions>
              </v-card>

              <!-- 21 点卡片 -->
              <v-card class="activity-card-enhanced">
                <v-card-title class="d-flex align-center">
                  <v-icon class="mr-2" color="pink">mdi-cards-playing</v-icon>
                  <span>21 点</span>
                  <v-spacer></v-spacer>
                  <v-chip
                    :color="blackjackEnabled ? 'success' : 'grey'"
                    size="small"
                    variant="flat"
                  >
                    <v-icon start size="12">
                      {{ blackjackEnabled ? 'mdi-check-circle' : 'mdi-pause-circle' }}
                    </v-icon>
                    {{ blackjackEnabled ? '运行中' : '已停用' }}
                  </v-chip>
                </v-card-title>

                <v-card-text>
                  <p class="text-body-2 text-medium-emphasis mb-4">
                    调整注额档位、参与门槛、抽水比率与拆分、庄家规则与超时时限，控制活动开关
                  </p>

                  <div class="activity-stats mb-4">
                    <v-row dense>
                      <v-col cols="4">
                        <div class="stat-item">
                          <div class="stat-value">{{ blackjackStats.total_hands || 0 }}</div>
                          <div class="stat-label">总手数</div>
                        </div>
                      </v-col>
                      <v-col cols="4">
                        <div class="stat-item">
                          <div class="stat-value text-success">{{ blackjackStats.total_players || 0 }}</div>
                          <div class="stat-label">参与人数</div>
                        </div>
                      </v-col>
                      <v-col cols="4">
                        <div class="stat-item">
                          <div class="stat-value text-info">{{ blackjackStats.today_hands || 0 }}</div>
                          <div class="stat-label">今日手数</div>
                        </div>
                      </v-col>
                    </v-row>

                    <v-row dense>
                      <v-col cols="4">
                        <div class="stat-item">
                          <div class="stat-value text-error">{{ blackjackStats.total_wagered?.toFixed(2) || '0.00' }}</div>
                          <div class="stat-label">累计押注</div>
                        </div>
                      </v-col>
                      <v-col cols="4">
                        <div class="stat-item">
                          <div class="stat-value text-warning">{{ blackjackStats.total_rake?.toFixed(2) || '0.00' }}</div>
                          <div class="stat-label">累计抽水</div>
                        </div>
                      </v-col>
                      <v-col cols="4">
                        <div class="stat-item">
                          <!-- 玩家视角：为负说明活动在净回收积分，与设计意图一致，故负值反而是好结果 -->
                          <div
                            class="stat-value"
                            :class="blackjackStats.net_credits > 0 ? 'text-error' : 'text-success'"
                          >
                            {{ blackjackStats.net_credits?.toFixed(2) || '0.00' }}
                          </div>
                          <div class="stat-label">玩家净变化</div>
                        </div>
                      </v-col>
                    </v-row>

                    <v-row dense>
                      <v-col cols="4">
                        <div class="stat-item">
                          <div class="stat-value text-secondary">{{ blackjackStats.active_hands || 0 }}</div>
                          <div class="stat-label">进行中</div>
                        </div>
                      </v-col>
                      <v-col cols="4">
                        <div class="stat-item">
                          <div class="stat-value text-warning">{{ blackjackStats.jackpot_balance?.toFixed(2) || '0.00' }}</div>
                          <div class="stat-label">奖池余额</div>
                        </div>
                      </v-col>
                      <v-col cols="4">
                        <div class="stat-item">
                          <div class="stat-value text-info">{{ blackjackStats.jackpot_paid?.toFixed(2) || '0.00' }}</div>
                          <div class="stat-label">奖池已派彩</div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </v-card-text>

                <v-card-actions class="pa-4 pt-0">
                  <v-btn
                    color="pink"
                    variant="elevated"
                    block
                    @click="openBlackjackManagement"
                  >
                    <v-icon start>mdi-cog</v-icon>
                    进入 21 点管理
                  </v-btn>
                </v-card-actions>
              </v-card>

              <!-- 竞拍活动卡片 -->
              <v-card class="activity-card-enhanced">
                <v-card-title class="d-flex align-center">
                  <v-icon class="mr-2" color="blue-darken-2">mdi-gavel</v-icon>
                  <span>竞拍活动</span>
                  <v-spacer></v-spacer>
                  <v-chip color="success" size="small" variant="flat">
                    <v-icon start size="12">mdi-check-circle</v-icon>
                    运行中
                  </v-chip>
                </v-card-title>
                
                <v-card-text>
                  <p class="text-body-2 text-medium-emphasis mb-4">
                    管理竞拍活动、设置拍品和起拍价格，查看竞拍统计数据
                  </p>
                  
                  <div class="activity-stats mb-4">
                    <v-row dense>
                      <v-col cols="6">
                        <div class="stat-item">
                          <div class="stat-value">{{ auctionStats.total_auctions || 0 }}</div>
                          <div class="stat-label">总竞拍数</div>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="stat-item">
                          <div class="stat-value">{{ auctionStats.active_auctions || 0 }}</div>
                          <div class="stat-label">进行中</div>
                        </div>
                      </v-col>
                    </v-row>
                    
                    <v-row dense>
                      <v-col cols="6">
                        <div class="stat-item">
                          <div class="stat-value">{{ auctionStats.total_bids || 0 }}</div>
                          <div class="stat-label">总出价次数</div>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="stat-item">
                          <div class="stat-value text-warning">{{ auctionStats.total_value?.toFixed(2) || '0.00' }}</div>
                          <div class="stat-label">总成交金额</div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </v-card-text>
                
                <v-card-actions class="pa-4 pt-0">
                  <v-btn
                    color="blue-darken-2"
                    variant="elevated"
                    block
                    @click="openAuctionManagement"
                  >
                    <v-icon start>mdi-gavel</v-icon>
                    进入竞拍管理
                  </v-btn>
                </v-card-actions>
              </v-card>

              <!-- 夺宝奇兵活动卡片 -->
              <v-card class="activity-card-enhanced">
                <v-card-title class="d-flex align-center">
                  <v-icon class="mr-2" color="deep-purple">mdi-treasure-chest</v-icon>
                  <span>夺宝奇兵</span>
                  <v-spacer></v-spacer>
                  <v-chip color="success" size="small" variant="flat">
                    <v-icon start size="12">mdi-check-circle</v-icon>
                    运行中
                  </v-chip>
                </v-card-title>

                <v-card-text>
                  <p class="text-body-2 text-medium-emphasis mb-4">
                    创建/管理夺宝期数，查看参与进度与开奖结果
                  </p>

                  <div class="activity-stats mb-4">
                    <v-row dense>
                      <v-col cols="6">
                        <div class="stat-item">
                          <div class="stat-value">{{ treasureStats.total_issues || 0 }}</div>
                          <div class="stat-label">期数总数</div>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="stat-item">
                          <div class="stat-value">{{ treasureStats.active_issues || 0 }}</div>
                          <div class="stat-label">进行中</div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </v-card-text>

                <v-card-actions class="pa-4 pt-0">
                  <v-btn
                    color="deep-purple"
                    variant="elevated"
                    block
                    @click="openTreasureManagement"
                  >
                    <v-icon start>mdi-treasure-chest</v-icon>
                    进入夺宝管理
                  </v-btn>
                </v-card-actions>
              </v-card>

              <!-- 其他活动卡片占位 -->
              <v-card class="activity-card-enhanced">
                <v-card-title class="d-flex align-center">
                  <v-icon class="mr-2" color="indigo">mdi-chart-line</v-icon>
                  <span>大预言家</span>
                  <v-spacer></v-spacer>
                  <v-chip color="success" size="small" variant="flat">
                    <v-icon start size="12">mdi-check-circle</v-icon>
                    运行中
                  </v-chip>
                </v-card-title>

                <v-card-text>
                  <p class="text-body-2 text-medium-emphasis mb-4">
                    创建预测题目、设置截止时间、关闭押注并裁决结算
                  </p>

                  <div class="activity-stats mb-4">
                    <v-row dense>
                      <v-col cols="6">
                        <div class="stat-item">
                          <div class="stat-value">{{ predictionStats.total_markets || 0 }}</div>
                          <div class="stat-label">题目总数</div>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="stat-item">
                          <div class="stat-value">{{ predictionStats.active_markets || 0 }}</div>
                          <div class="stat-label">押注中</div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </v-card-text>

                <v-card-actions class="pa-4 pt-0">
                  <v-btn
                    color="indigo"
                    variant="elevated"
                    block
                    @click="openPredictionManagement"
                  >
                    <v-icon start>mdi-chart-line</v-icon>
                    进入预测管理
                  </v-btn>
                </v-card-actions>
              </v-card>

              <!-- 礼包活动卡片 -->
              <v-card class="activity-card-enhanced">
                <v-card-title class="d-flex align-center">
                  <v-icon class="mr-2" color="pink-darken-1">mdi-gift</v-icon>
                  <span>礼包</span>
                  <v-spacer></v-spacer>
                  <v-chip color="success" size="small" variant="flat">
                    <v-icon start size="12">mdi-check-circle</v-icon>
                    运行中
                  </v-chip>
                </v-card-title>

                <v-card-text>
                  <p class="text-body-2 text-medium-emphasis mb-4">
                    发布带时间窗、领取资格与限量的运营福利，查看领取进度与发放统计
                  </p>

                  <div class="activity-stats mb-4">
                    <v-row dense>
                      <v-col cols="6">
                        <div class="stat-item">
                          <div class="stat-value">{{ giftPackStats.total_packs || 0 }}</div>
                          <div class="stat-label">礼包总数</div>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="stat-item">
                          <div class="stat-value text-success">{{ giftPackStats.active_packs || 0 }}</div>
                          <div class="stat-label">进行中</div>
                        </div>
                      </v-col>
                    </v-row>
                    <v-row dense class="mt-2">
                      <v-col cols="6">
                        <div class="stat-item">
                          <div class="stat-value text-info">{{ giftPackStats.total_claims || 0 }}</div>
                          <div class="stat-label">累计领取</div>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="stat-item">
                          <div class="stat-value text-warning">{{ giftPackStats.upcoming_packs || 0 }}</div>
                          <div class="stat-label">未开始</div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </v-card-text>

                <v-card-actions class="pa-4 pt-0">
                  <v-btn
                    color="pink-darken-1"
                    variant="elevated"
                    block
                    @click="openGiftPackManagement"
                  >
                    <v-icon start>mdi-gift</v-icon>
                    进入礼包管理
                  </v-btn>
                </v-card-actions>
              </v-card>

              <v-card class="activity-card-enhanced activity-placeholder">
                <v-card-title class="d-flex align-center">
                  <v-icon class="mr-2" color="grey-lighten-1">mdi-plus-circle-outline</v-icon>
                  <span>新活动</span>
                  <v-spacer></v-spacer>
                  <v-chip color="grey-lighten-1" size="small" variant="flat">
                    即将推出
                  </v-chip>
                </v-card-title>
                
                <v-card-text>
                  <p class="text-body-2 text-medium-emphasis mb-4">
                    更多精彩活动正在开发中，敬请期待
                  </p>
                  
                  <div class="text-center">
                    <v-icon size="48" color="grey-lighten-2">mdi-gift-outline</v-icon>
                  </div>
                </v-card-text>
                
                <v-card-actions class="pa-4 pt-0">
                  <v-btn
                    color="grey-lighten-1"
                    variant="outlined"
                    block
                    disabled
                  >
                    <v-icon start>mdi-clock-outline</v-icon>
                    敬请期待
                  </v-btn>
                </v-card-actions>
              </v-card>
            </div>

            <!-- 夺宝管理弹窗 -->
            <v-dialog v-model="showTreasureManagement" max-width="900" persistent>
              <v-card class="activity-dialog">
                <v-card-title class="d-flex align-center justify-space-between">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="deep-purple">mdi-treasure-chest</v-icon>
                    夺宝期数创建 / 管理
                  </div>
                  <div>
                    <v-btn icon @click="loadTreasureStats" :loading="treasureIssuesLoading">
                      <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                    <v-btn icon @click="closeTreasureManagement">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </div>
                </v-card-title>

                <v-divider />

                <v-card-text class="pa-6">
                  <v-alert v-if="treasureIssuesError" type="error" variant="tonal" class="mb-4">
                    {{ treasureIssuesError }}
                  </v-alert>

                  <v-row>
                    <v-col cols="12" md="5">
                      <v-card variant="outlined" rounded="lg">
                        <v-card-title class="text-subtitle-1">创建新期数</v-card-title>
                        <v-card-text>
                          <v-text-field
                            v-model="createTreasureForm.title"
                            label="标题（可选）"
                            variant="outlined"
                            density="compact"
                            placeholder="留空将自动生成"
                          />
                          <v-textarea v-model="createTreasureForm.description" label="描述（可选）" variant="outlined" density="compact" rows="3" />
                          <v-text-field v-model="createTreasureForm.prize_credits" label="奖池积分" type="number" variant="outlined" density="compact" />
                          <v-text-field v-model="createTreasureForm.total_credits_required" label="总需积分" type="number" variant="outlined" density="compact" />
                          <v-text-field v-model="createTreasureForm.credits_per_share" label="每份积分" type="number" variant="outlined" density="compact" />
                          <v-text-field
                            v-model="createTreasureForm.start_number"
                            label="起始幸运号（可选）"
                            type="number"
                            variant="outlined"
                            density="compact"
                            placeholder="留空则随机生成"
                          />

                          <v-btn color="deep-purple" variant="elevated" block @click="submitCreateTreasureIssue">
                            <v-icon start>mdi-plus</v-icon>
                            创建期数
                          </v-btn>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="7">
                      <v-card variant="outlined" rounded="lg">
                        <v-card-title class="text-subtitle-1">期数列表</v-card-title>
                        <v-card-text>
                          <div v-if="treasureIssuesLoading" class="text-center py-6">
                            <v-progress-circular indeterminate color="primary" size="32" />
                          </div>
                          <div v-else>
                            <v-list density="compact">
                              <v-list-item v-for="it in treasureIssues" :key="it.id">
                                <v-list-item-title>
                                  #{{ it.id }} · {{ it.title }}
                                </v-list-item-title>
                                <v-list-item-subtitle>
                                  进度 {{ it.shares_sold }}/{{ it.total_shares }}
                                </v-list-item-subtitle>
                                <template #append>
                                  <div class="treasure-issue-actions">
                                    <v-chip
                                      size="small"
                                      :color="it.status === 1 ? 'deep-purple' : (it.status === 2 ? 'success' : 'grey')"
                                      variant="flat"
                                      class="treasure-issue-status"
                                    >
                                      {{ it.status === 1 ? '进行中' : (it.status === 2 ? '已开奖' : '已取消') }}
                                    </v-chip>

                                    <v-chip
                                      v-if="it.status === 1"
                                      size="small"
                                      color="error"
                                      variant="tonal"
                                      class="treasure-issue-cancel"
                                      :disabled="deletingTreasureIssueId !== null"
                                      @click.stop="confirmCancelTreasureIssue(it)"
                                    >
                                      <v-icon start size="14">mdi-trash-can-outline</v-icon>
                                      取消
                                    </v-chip>
                                  </div>
                                </template>
                              </v-list-item>
                            </v-list>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-dialog>

            <!-- 预测管理弹窗 -->
            <v-dialog v-model="showPredictionManagement" max-width="1000" persistent>
              <v-card class="activity-dialog">
                <v-card-title class="d-flex align-center justify-space-between">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="indigo">mdi-chart-line</v-icon>
                    预测题目创建 / 管理
                  </div>
                  <div>
                    <v-btn icon @click="loadPredictionStats" :loading="predictionMarketsLoading">
                      <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                    <v-btn icon @click="closePredictionManagement">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </div>
                </v-card-title>

                <v-divider />

                <v-card-text class="pa-6">
                  <v-alert v-if="predictionMarketsError" type="error" variant="tonal" class="mb-4">
                    {{ predictionMarketsError }}
                  </v-alert>

                  <v-row>
                    <v-col cols="12" md="5">
                      <v-card variant="outlined" rounded="lg">
                        <v-card-title class="text-subtitle-1">创建新题目</v-card-title>
                        <v-card-text>
                          <v-text-field
                            v-model="createPredictionForm.title"
                            label="标题"
                            variant="outlined"
                            density="compact"
                            required
                          />
                          <v-textarea
                            v-model="createPredictionForm.description"
                            label="描述（可选）"
                            variant="outlined"
                            density="compact"
                            rows="3"
                          />
                          <v-text-field
                            v-model="createPredictionForm.betting_deadline"
                            label="押注截止时间"
                            type="datetime-local"
                            variant="outlined"
                            density="compact"
                            required
                          />
                          <v-text-field v-model="createPredictionForm.virtual_yes_pool" label="YES 虚拟池" type="number" variant="outlined" density="compact" />
                          <v-text-field v-model="createPredictionForm.virtual_no_pool" label="NO 虚拟池" type="number" variant="outlined" density="compact" />
                          <v-text-field v-model="createPredictionForm.max_bet_per_user" label="单用户上限" type="number" variant="outlined" density="compact" />
                          <v-btn color="indigo" variant="elevated" block @click="submitCreatePredictionMarket">
                            <v-icon start>mdi-plus</v-icon>
                            创建题目
                          </v-btn>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <v-col cols="12" md="7">
                      <v-card variant="outlined" rounded="lg">
                        <v-card-title class="text-subtitle-1">题目列表</v-card-title>
                        <v-card-text>
                          <div v-if="predictionMarketsLoading" class="text-center py-6">
                            <v-progress-circular indeterminate color="primary" size="32" />
                          </div>
                          <div v-else>
                            <v-list density="compact">
                              <v-list-item v-for="it in predictionMarkets" :key="it.id">
                                <v-list-item-title>
                                  #{{ it.id }} · {{ it.title }}
                                </v-list-item-title>
                                <v-list-item-subtitle>
                                  YES {{ it.real_yes_pool }} / NO {{ it.real_no_pool }} · 截止 {{ formatDateTimeByTs(it.betting_deadline) }}
                                </v-list-item-subtitle>
                                <template #append>
                                  <div class="treasure-issue-actions">
                                    <v-chip
                                      size="small"
                                      :color="it.status === 1 ? 'indigo' : (it.status === 2 ? 'orange' : (it.status === 3 ? 'success' : 'grey'))"
                                      variant="flat"
                                      class="treasure-issue-status"
                                    >
                                      {{ it.status === 1 ? '押注中' : (it.status === 2 ? '已截止' : (it.status === 3 ? '已结算' : '已取消')) }}
                                    </v-chip>

                                    <v-chip
                                      v-if="it.status === 1"
                                      size="small"
                                      color="warning"
                                      variant="tonal"
                                      class="treasure-issue-cancel"
                                      :disabled="closingPredictionMarketId !== null || resolvingPredictionMarketId !== null"
                                      @click.stop="submitClosePredictionMarket(it)"
                                    >
                                      <v-icon start size="14">mdi-lock</v-icon>
                                      截止
                                    </v-chip>

                                    <v-chip
                                      v-if="it.status === 2"
                                      size="small"
                                      color="success"
                                      variant="tonal"
                                      class="treasure-issue-cancel"
                                      :disabled="closingPredictionMarketId !== null || resolvingPredictionMarketId !== null"
                                      @click.stop="submitResolvePredictionMarket(it, 1)"
                                    >
                                      <v-icon start size="14">mdi-check</v-icon>
                                      YES 胜
                                    </v-chip>

                                    <v-chip
                                      v-if="it.status === 2"
                                      size="small"
                                      color="error"
                                      variant="tonal"
                                      class="treasure-issue-cancel"
                                      :disabled="closingPredictionMarketId !== null || resolvingPredictionMarketId !== null"
                                      @click.stop="submitResolvePredictionMarket(it, 0)"
                                    >
                                      <v-icon start size="14">mdi-close</v-icon>
                                      NO 胜
                                    </v-chip>
                                  </div>
                                </template>
                              </v-list-item>
                            </v-list>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-dialog>
          </v-window-item>

          <!-- 概览 Tab -->
          <v-window-item value="overview">
            <!-- 加载状态 -->
            <div v-if="systemStatsLoading || premiumStatsLoading || trafficOverviewLoading" class="text-center my-10">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <div class="mt-3">加载系统统计中...</div>
            </div>
            
            <!-- 错误状态 -->
            <div v-else-if="systemStatsError" class="text-center my-10">
              <v-alert type="error">{{ systemStatsError }}</v-alert>
              <v-btn 
                color="primary" 
                variant="outlined" 
                class="mt-3"
                @click="refreshOverviewStats"
              >
                重试
              </v-btn>
            </div>
            
            <!-- 系统统计内容 -->
            <div v-else>
              <v-card class="admin-card-enhanced mb-4">
                <v-card-title class="text-center">
                  <v-icon start color="info">mdi-account-group</v-icon> 用户统计
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" sm="6" md="3">
                      <div class="stat-item">
                        <div class="stat-value text-orange-darken-2">{{ systemStats.plex_users }}</div>
                        <div class="stat-label">Plex 用户</div>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="stat-item">
                        <div class="stat-value text-green-darken-2">{{ systemStats.emby_users }}</div>
                        <div class="stat-label">Emby 用户</div>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="stat-item">
                        <div class="stat-value text-primary">{{ systemStats.total_users }}</div>
                        <div class="stat-label">总用户数</div>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                      <div class="stat-item">
                        <div class="stat-value text-red-darken-2">{{ systemStats.nsfw_unlocked_users || 0 }}</div>
                        <div class="stat-label">NSFW 解锁</div>
                      </div>
                    </v-col>
                  </v-row>
                  <v-row class="mt-2">
                    <v-col cols="12" sm="6" md="4">
                      <div class="stat-item">
                        <div class="stat-value text-indigo-darken-2">{{ systemStats.line_schedule_unlocked_users || 0 }}</div>
                        <div class="stat-label">线路调度解锁</div>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <div class="stat-item">
                        <div class="stat-value text-purple-darken-2">{{ systemStats.vaultwarden_redeemed_count || 0 }}</div>
                        <div class="stat-label">Bitwarden 兑换数</div>
                      </div>
                    </v-col>
                    <v-col cols="12" sm="6" md="4">
                      <div class="stat-item">
                        <div class="stat-value text-teal-darken-2">{{ systemStats.download_unlocked_users || 0 }}</div>
                        <div class="stat-label">下载权限解锁</div>
                      </div>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
              
              <!-- Premium 用户统计卡片 -->
              <v-card class="admin-card-enhanced mb-4">
                <v-card-title class="text-center">
                  <v-icon start color="purple">mdi-crown</v-icon> Premium 用户统计
                </v-card-title>
                <v-card-text>
                  <!-- 加载状态 -->
                  <div v-if="premiumStatsLoading" class="text-center py-4">
                    <v-progress-circular indeterminate size="small" color="purple"></v-progress-circular>
                    <div class="mt-2">加载Premium统计中...</div>
                  </div>
                  
                  <!-- 错误状态 -->
                  <div v-else-if="premiumStatsError" class="text-center py-4">
                    <v-alert type="error" density="compact">{{ premiumStatsError }}</v-alert>
                    <v-btn 
                      color="purple" 
                      variant="outlined" 
                      size="small"
                      class="mt-2"
                      @click="fetchPremiumStats"
                    >
                      重试
                    </v-btn>
                  </div>
                  
                  <!-- Premium统计数据 -->
                  <div v-else>
                    <v-row>
                      <v-col cols="12" sm="4">
                        <div class="stat-item">
                          <div class="stat-value text-purple">{{ premiumStats.active_premium_users }}</div>
                          <div class="stat-label">活跃 Premium 用户</div>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="4">
                        <div class="stat-item">
                          <div class="stat-value text-orange-darken-2">{{ premiumStats.premium_plex_users }}</div>
                          <div class="stat-label">Plex Premium 用户</div>
                        </div>
                      </v-col>
                      <v-col cols="12" sm="4">
                        <div class="stat-item">
                          <div class="stat-value text-green-darken-2">{{ premiumStats.premium_emby_users }}</div>
                          <div class="stat-label">Emby Premium 用户</div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </v-card-text>
              </v-card>
              
              <!-- 全平台流量统计卡片 -->
              <v-card class="admin-card-enhanced mb-4">
                <v-card-title class="text-center">
                  <v-icon start color="blue-darken-2">mdi-chart-bar</v-icon> 全站流量统计
                </v-card-title>
                <v-card-text>
                  <!-- 加载状态 -->
                  <div v-if="trafficOverviewLoading" class="text-center py-4">
                    <v-progress-circular indeterminate size="small" color="blue-darken-2"></v-progress-circular>
                    <div class="mt-2">加载流量概览中...</div>
                  </div>
                  
                  <!-- 错误状态 -->
                  <div v-else-if="trafficOverviewError" class="text-center py-4">
                    <v-alert type="error" density="compact">{{ trafficOverviewError }}</v-alert>
                    <v-btn 
                      color="blue-darken-2" 
                      variant="outlined" 
                      size="small"
                      class="mt-2"
                      @click="fetchTrafficOverview"
                    >
                      重试
                    </v-btn>
                  </div>
                  
                  <!-- 流量概览数据 -->
                  <div v-else>
                    <!-- 今日流量统计 -->
                    <div class="mb-6">
                      <div class="d-flex align-center mb-3">
                        <v-icon size="20" color="success" class="mr-2">mdi-calendar-today</v-icon>
                        <span class="text-subtitle1 font-weight-medium">今日流量</span>
                        <v-spacer></v-spacer>
                        <v-chip size="small" color="success" variant="tonal">
                          <v-icon start size="12">mdi-trending-up</v-icon>
                          实时
                        </v-chip>
                      </div>
                      <v-row>
                        <v-col cols="12" sm="4">
                          <div class="stat-item">
                            <div class="stat-value text-primary">{{ formatTrafficSize(trafficOverview.today.total) }}</div>
                            <div class="stat-label">总计</div>
                          </div>
                        </v-col>
                        <v-col cols="12" sm="4">
                          <div class="stat-item">
                            <div class="stat-value text-green-darken-2">{{ formatTrafficSize(trafficOverview.today.emby) }}</div>
                            <div class="stat-label">Emby 流量</div>
                          </div>
                        </v-col>
                        <v-col cols="12" sm="4">
                          <div class="stat-item">
                            <div class="stat-value text-orange-darken-2">{{ formatTrafficSize(trafficOverview.today.plex) }}</div>
                            <div class="stat-label">Plex 流量</div>
                          </div>
                        </v-col>
                      </v-row>
                    </div>
                    
                    <!-- 本周流量统计 -->
                    <div class="mb-6">
                      <div class="d-flex align-center mb-3">
                        <v-icon size="20" color="info" class="mr-2">mdi-calendar-week</v-icon>
                        <span class="text-subtitle1 font-weight-medium">本周流量</span>
                        <v-spacer></v-spacer>
                        <v-chip size="small" color="info" variant="tonal">
                          <v-icon start size="12">mdi-chart-timeline-variant</v-icon>
                          周统计
                        </v-chip>
                      </div>
                      <v-row>
                        <v-col cols="12" sm="4">
                          <div class="stat-item">
                            <div class="stat-value text-primary">{{ formatTrafficSize(trafficOverview.week.total) }}</div>
                            <div class="stat-label">总计</div>
                          </div>
                        </v-col>
                        <v-col cols="12" sm="4">
                          <div class="stat-item">
                            <div class="stat-value text-green-darken-2">{{ formatTrafficSize(trafficOverview.week.emby) }}</div>
                            <div class="stat-label">Emby 流量</div>
                          </div>
                        </v-col>
                        <v-col cols="12" sm="4">
                          <div class="stat-item">
                            <div class="stat-value text-orange-darken-2">{{ formatTrafficSize(trafficOverview.week.plex) }}</div>
                            <div class="stat-label">Plex 流量</div>
                          </div>
                        </v-col>
                      </v-row>
                    </div>
                    
                    <!-- 本月流量统计 -->
                    <div>
                      <div class="d-flex align-center mb-3">
                        <v-icon size="20" color="deep-purple" class="mr-2">mdi-calendar-month</v-icon>
                        <span class="text-subtitle1 font-weight-medium">本月流量</span>
                        <v-spacer></v-spacer>
                        <v-chip size="small" color="deep-purple" variant="tonal">
                          <v-icon start size="12">mdi-chart-box</v-icon>
                          月度汇总
                        </v-chip>
                      </div>
                      <v-row>
                        <v-col cols="12" sm="4">
                          <div class="stat-item">
                            <div class="stat-value text-primary">{{ formatTrafficSize(trafficOverview.month.total) }}</div>
                            <div class="stat-label">总计</div>
                          </div>
                        </v-col>
                        <v-col cols="12" sm="4">
                          <div class="stat-item">
                            <div class="stat-value text-green-darken-2">{{ formatTrafficSize(trafficOverview.month.emby) }}</div>
                            <div class="stat-label">Emby 流量</div>
                          </div>
                        </v-col>
                        <v-col cols="12" sm="4">
                          <div class="stat-item">
                            <div class="stat-value text-orange-darken-2">{{ formatTrafficSize(trafficOverview.month.plex) }}</div>
                            <div class="stat-label">Plex 流量</div>
                          </div>
                        </v-col>
                      </v-row>
                    </div>
                    
                    <!-- 查看线路详情按钮 -->
                    <div class="text-center mt-4">
                      <v-btn 
                        color="blue-darken-2" 
                        variant="outlined" 
                        size="small"
                        @click="showLineTrafficDialog = true"
                      >
                        <v-icon start>mdi-lan</v-icon>
                        查看线路流量详情
                      </v-btn>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
              
              <!-- Premium 线路流量统计卡片 -->
              <v-card class="admin-card-enhanced mb-4">
                <v-card-title class="text-center">
                  <v-icon start color="deep-purple">mdi-chart-line</v-icon> Premium 线路流量统计
                </v-card-title>
                <v-card-text>
                  <!-- 加载状态 -->
                  <div v-if="trafficStatsLoading" class="text-center py-4">
                    <v-progress-circular indeterminate size="small" color="deep-purple"></v-progress-circular>
                    <div class="mt-2">加载流量统计中...</div>
                  </div>
                  
                  <!-- 错误状态 -->
                  <div v-else-if="trafficStatsError" class="text-center py-4">
                    <v-alert type="error" density="compact">{{ trafficStatsError }}</v-alert>
                    <v-btn 
                      color="deep-purple" 
                      variant="outlined" 
                      size="small"
                      class="mt-2"
                      @click="fetchTrafficStats"
                    >
                      重试
                    </v-btn>
                  </div>
                  
                  <!-- 无数据状态 -->
                  <div v-else-if="!trafficStats || trafficStats.length === 0" class="text-center py-4">
                    <v-alert type="info" density="compact">
                      暂无 Premium 线路流量数据
                    </v-alert>
                  </div>
                  
                  <!-- 流量统计数据 -->
                  <div v-else>
                    <div class="premium-traffic-grid">
                      <div 
                        v-for="lineStat in trafficStats" 
                        :key="lineStat.line"
                        class="premium-line-card"
                      >
                        <!-- 线路卡片标题栏 -->
                        <div class="premium-line-header">
                          <div class="line-title-wrapper">
                            <v-icon color="deep-purple" class="line-icon">mdi-server-network</v-icon>
                            <v-tooltip :text="lineStat.line" location="top">
                              <template v-slot:activator="{ props }">
                                <h3 class="line-title" v-bind="props">{{ lineStat.line }}</h3>
                              </template>
                            </v-tooltip>
                          </div>
                          <v-chip size="small" color="deep-purple" variant="tonal" class="status-chip">
                            <v-icon start size="12">mdi-check-circle</v-icon>
                            运行中
                          </v-chip>
                        </div>
                        
                        <!-- 流量统计区域 -->
                        <div class="traffic-stats-section">
                          <div class="stats-header">
                            <v-icon size="16" color="deep-purple" class="mr-2">mdi-chart-line</v-icon>
                            <span class="section-title">流量统计</span>
                          </div>
                          
                          <div class="traffic-metrics">
                            <div class="metric-item today">
                              <div class="metric-label">
                                <v-icon size="12" color="success">mdi-calendar-today</v-icon>
                                今日
                              </div>
                              <div class="metric-value success">
                                {{ formatTrafficSize(lineStat.today_traffic) }}
                              </div>
                            </div>
                            
                            <div class="metric-item week">
                              <div class="metric-label">
                                <v-icon size="12" color="info">mdi-calendar-week</v-icon>
                                本周
                              </div>
                              <div class="metric-value info">
                                {{ formatTrafficSize(lineStat.week_traffic) }}
                              </div>
                            </div>
                            
                            <div class="metric-item month">
                              <div class="metric-label">
                                <v-icon size="12" color="warning">mdi-calendar-month</v-icon>
                                本月
                              </div>
                              <div class="metric-value warning">
                                {{ formatTrafficSize(lineStat.month_traffic) }}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <!-- 用户排行区域 -->
                        <div v-if="lineStat.top_users && lineStat.top_users.length > 0" class="ranking-section">
                          <div class="stats-header">
                            <v-icon size="16" color="amber-darken-2" class="mr-2">mdi-trophy</v-icon>
                            <span class="section-title">流量排行</span>
                            <v-chip size="x-small" color="amber-darken-2" variant="tonal" class="ml-2">
                              今日
                            </v-chip>
                          </div>
                          
                          <div class="ranking-list">
                            <div 
                              v-for="(user, index) in lineStat.top_users.slice(0, 3)" 
                              :key="user.username"
                              class="ranking-item"
                            >
                              <div class="user-info">
                                <div class="rank-badge" :class="`rank-${index + 1}`">
                                  <v-icon 
                                    :color="getUserRankColor(index)" 
                                    size="14"
                                  >
                                    {{ getUserRankIcon(index) }}
                                  </v-icon>
                                </div>
                                <span class="username">{{ formatUsername(user.username) }}</span>
                              </div>
                              <div class="traffic-amount">
                                {{ formatTrafficSize(user.traffic) }}
                              </div>
                            </div>
                            
                            <!-- 查看更多按钮 -->
                            <div v-if="lineStat.top_users.length > 3" class="view-more-section">
                              <v-btn
                                size="small"
                                variant="outlined"
                                color="deep-purple"
                                class="view-more-btn"
                                @click="showLineUsersDialog(lineStat)"
                              >
                                <v-icon start size="16">mdi-account-group</v-icon>
                                查看全部 {{ lineStat.top_users.length }} 用户
                              </v-btn>
                            </div>
                          </div>
                        </div>
                        
                        <!-- 无数据状态 -->
                        <div v-else class="no-data-section">
                          <div class="no-data-content">
                            <v-icon size="32" color="grey-lighten-1">mdi-account-off-outline</v-icon>
                            <p class="no-data-text">暂无用户使用数据</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
              
              <!-- 系统信息卡片 -->
              <v-card class="admin-card-enhanced">
                <v-card-title class="text-center">
                  <v-icon start color="blue">mdi-information</v-icon> 系统信息
                </v-card-title>
                <v-card-text class="text-center">
                  <p class="text-body-1 mb-3">系统运行状态良好</p>
                  <v-chip color="success" variant="flat">
                    <v-icon start>mdi-check-circle</v-icon>
                    正常运行
                  </v-chip>
                </v-card-text>
              </v-card>
            </div>
          </v-window-item>
        </v-window>
      </div>
    </div>

    <!-- 对话框组件 -->
    <donation-dialog
      ref="donationDialog"
      @donation-submitted="handleDonationSubmitted"
    />
    
    <donation-registration-management-dialog
      ref="donationRegistrationDialog"
      @registration-processed="handleRegistrationProcessed"
    />
    
    <admin-invite-code-dialog
      ref="inviteCodeDialog"
      @invite-codes-generated="handleInviteCodesGenerated"
    />
    
    <line-management-dialog
      ref="lineManagementDialog"
      @lines-updated="handleLinesUpdated"
      @tags-updated="handleTagsUpdated"
    />

    <!-- Crypto 捐赠管理对话框 -->
    <v-dialog v-model="showCryptoDonationDialog" max-width="1200px" persistent>
      <v-card>
        <v-toolbar color="purple-darken-2" dark flat>
          <v-btn icon dark @click="closeCryptoDonationDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>
            <v-icon class="mr-2">mdi-bitcoin</v-icon>
            Crypto 捐赠记录管理
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="fetchCryptoDonationOrders">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-toolbar>
        
        <v-card-text class="pa-6">
          <!-- 筛选控件 -->
          <v-row class="mb-4">
            <v-col cols="12" md="4">
              <v-select
                v-model="cryptoDonationStatusFilter"
                :items="[
                  { title: '全部状态', value: null },
                  { title: '等待支付', value: '1' },
                  { title: '支付成功', value: '2' },
                  { title: '已过期', value: '3' }
                ]"
                item-title="title"
                item-value="value"
                label="状态筛选"
                variant="outlined"
                density="compact"
                @update:model-value="onCryptoDonationStatusFilterChange"
              ></v-select>
            </v-col>
            <v-col cols="12" md="8" class="d-flex align-center">
              <v-spacer></v-spacer>
              <v-chip v-if="cryptoDonationTotal > 0" color="primary" variant="tonal" class="mr-2">
                <v-icon start size="16">mdi-database</v-icon>
                共 {{ cryptoDonationTotal }} 条记录
              </v-chip>
            </v-col>
          </v-row>
          
          <!-- 加载状态 -->
          <div v-if="cryptoDonationLoading" class="text-center py-8">
            <v-progress-circular indeterminate color="purple-darken-2"></v-progress-circular>
            <div class="mt-2">加载 Crypto 捐赠记录中...</div>
          </div>
          
          <!-- 错误状态 -->
          <div v-else-if="cryptoDonationError" class="text-center py-8">
            <v-alert type="error" density="compact">{{ cryptoDonationError }}</v-alert>
            <v-btn 
              color="purple-darken-2" 
              variant="outlined" 
              class="mt-3"
              @click="fetchCryptoDonationOrders"
            >
              重试
            </v-btn>
          </div>
          
          <!-- 无数据 -->
          <div v-else-if="cryptoDonationOrders.length === 0" class="text-center py-8">
            <v-icon size="64" color="grey-lighten-1">mdi-database-off</v-icon>
            <div class="text-grey-darken-1 mt-2">暂无 Crypto 捐赠记录</div>
          </div>
          
          <!-- 数据表格 -->
          <v-data-table
            v-else
            :headers="[
              { title: 'ID', key: 'id', sortable: true, width: 80 },
              { title: '用户信息', key: 'user_info', sortable: true, width: 150 },
              { title: '订单号', key: 'order_id', sortable: true, width: 200 },
              { title: '加密货币', key: 'crypto_type', sortable: true, width: 130 },
              { title: '金额(CNY)', key: 'amount', sortable: true, width: 120 },
              { title: '实际金额', key: 'actual_amount', sortable: true, width: 120 },
              { title: '状态', key: 'status', sortable: true, width: 100 },
              { title: '创建时间', key: 'created_at', sortable: true, width: 150 },
              { title: '完成时间', key: 'paid_at', sortable: true, width: 150 },
              { title: '备注', key: 'note', sortable: false, width: 120 }
            ]"
            :items="cryptoDonationOrders"
            :loading="cryptoDonationLoading"
            density="compact"
            class="crypto-donation-table"
            :items-per-page="cryptoDonationPerPage"
            hide-default-footer
          >
            <template #item.user_info="{ item }">
              <div>
                <div class="text-body-2 font-weight-medium">
                  {{ item.username ? `@${item.username}` : `用户 ${item.user_id}` }}
                </div>
                <div class="text-caption text-medium-emphasis">
                  ID: {{ item.user_id }}
                </div>
              </div>
            </template>
            
            <template #item.order_id="{ item }">
              <div class="text-truncate" style="max-width: 180px;" :title="item.order_id">
                {{ item.order_id }}
              </div>
            </template>
            
            <template #item.crypto_type="{ item }">
              <v-chip size="small" color="purple" variant="tonal">
                <v-icon start size="12">mdi-bitcoin</v-icon>
                {{ item.crypto_type }}
              </v-chip>
            </template>
            
            <template #item.amount="{ item }">
              <span class="text-success font-weight-bold">
                ¥{{ item.amount?.toFixed(2) || '0.00' }}
              </span>
            </template>
            
            <template #item.actual_amount="{ item }">
              <span v-if="item.actual_amount" class="text-primary font-weight-bold">
                ¥{{ item.actual_amount.toFixed(2) }}
              </span>
              <span v-else class="text-grey">-</span>
            </template>
            
            <template #item.status="{ item }">
              <v-chip
                :color="getOrderStatusInfo(item.status).color"
                size="small"
                variant="flat"
              >
                <v-icon start size="12">{{ getOrderStatusInfo(item.status).icon }}</v-icon>
                {{ getOrderStatusInfo(item.status).text }}
              </v-chip>
            </template>
            
            <template #item.created_at="{ item }">
              <div class="text-caption">
                {{ formatDateTime(item.created_at) }}
              </div>
            </template>
            
            <template #item.paid_at="{ item }">
              <div v-if="item.paid_at" class="text-caption text-success">
                {{ formatDateTime(item.paid_at) }}
              </div>
              <span v-else class="text-grey">-</span>
            </template>
            
            <template #item.note="{ item }">
              <div v-if="item.note" class="text-truncate" style="max-width: 100px;" :title="item.note">
                {{ item.note }}
              </div>
              <span v-else class="text-grey">-</span>
            </template>
          </v-data-table>
          
          <!-- 分页 -->
          <div v-if="cryptoDonationTotal > cryptoDonationPerPage" class="d-flex justify-center mt-4">
            <v-pagination
              v-model="cryptoDonationPage"
              :length="Math.ceil(cryptoDonationTotal / cryptoDonationPerPage)"
              :total-visible="7"
              @update:model-value="onCryptoDonationPageChange"
            ></v-pagination>
          </div>
        </v-card-text>
        
        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn @click="closeCryptoDonationDialog" variant="outlined">
            关闭
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 转盘管理弹窗 -->
    <v-dialog 
      v-model="showWheelManagement" 
      fullscreen
      transition="dialog-bottom-transition"
      :persistent="true"
    >
      <v-card>
        <v-toolbar color="purple-darken-2" dark>
          <v-btn icon dark @click="closeWheelManagement">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>
            <v-icon class="mr-2">mdi-ferris-wheel</v-icon>
            幸运大转盘管理
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="refreshWheelStats">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-toolbar>
        
        <div style="height: calc(100vh - 64px); overflow-y: auto;">
          <WheelAdminPanel @show-message="showMessage" />
        </div>
      </v-card>
    </v-dialog>

    <!-- 21 点管理弹窗 -->
    <v-dialog
      v-model="showBlackjackManagement"
      fullscreen
      transition="dialog-bottom-transition"
      :persistent="true"
    >
      <v-card>
        <v-toolbar color="pink" dark>
          <v-btn icon dark @click="closeBlackjackManagement">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>
            <v-icon class="mr-2">mdi-cards-playing</v-icon>
            21 点管理
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="refreshBlackjackPanel">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-toolbar>

        <div style="height: calc(100vh - 64px); overflow-y: auto;">
          <v-tabs v-model="blackjackTab" color="pink" grow>
            <v-tab value="cash">现金局</v-tab>
            <v-tab value="tournament">锦标赛</v-tab>
          </v-tabs>
          <v-window v-model="blackjackTab">
            <v-window-item value="cash">
              <BlackjackAdminPanel ref="blackjackAdminPanel" @show-message="showMessage" />
            </v-window-item>
            <v-window-item value="tournament">
              <BlackjackTournamentAdminPanel
                ref="blackjackTournamentAdminPanel"
                @show-message="showMessage"
              />
            </v-window-item>
          </v-window>
        </div>
      </v-card>
    </v-dialog>

    <!-- 礼包管理弹窗 -->
    <v-dialog
      v-model="showGiftPackManagement"
      fullscreen
      transition="dialog-bottom-transition"
      :persistent="true"
    >
      <v-card>
        <v-toolbar color="pink-darken-1" dark>
          <v-btn icon dark @click="closeGiftPackManagement">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>
            <v-icon class="mr-2">mdi-gift</v-icon>
            礼包管理
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="refreshGiftPackPanel">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-toolbar>

        <div style="height: calc(100vh - 64px); overflow-y: auto;">
          <GiftPackAdminPanel ref="giftPackAdminPanel" @changed="loadGiftPackStats" />
        </div>
      </v-card>
    </v-dialog>

    <!-- 竞拍管理弹窗 -->
    <v-dialog 
      v-model="showAuctionManagement" 
      fullscreen
      transition="dialog-bottom-transition"
      :persistent="true"
    >
      <v-card>
        <v-toolbar color="blue-darken-2" dark>
          <v-btn icon dark @click="closeAuctionManagement">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>
            <v-icon class="mr-2">mdi-gavel</v-icon>
            竞拍管理
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="refreshAuctionStats">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-toolbar>
        
        <div style="height: calc(100vh - 64px); overflow-y: auto; padding: 20px;">
          <!-- 权限检查 -->
          <div v-if="!isAdmin" class="text-center my-10">
            <v-alert type="warning">
              权限不足，需要管理员权限才能访问竞拍管理
            </v-alert>
          </div>
          
          <!-- 竞拍管理内容 -->
          <div v-else class="auction-management">
            <!-- 竞拍统计卡片 -->
            <v-card class="admin-card-enhanced mb-6">
              <v-card-title class="d-flex align-center">
                <v-icon class="mr-2" color="primary">mdi-chart-line</v-icon>
                <span>竞拍统计</span>
                <v-spacer></v-spacer>
                <v-btn 
                  @click="refreshAuctionStats" 
                  :loading="auctionStatsLoading"
                  size="small"
                  variant="text"
                  icon="mdi-refresh"
                ></v-btn>
              </v-card-title>
              
              <v-card-text>
                <div v-if="auctionStatsLoading" class="text-center py-4">
                  <v-progress-circular indeterminate size="small" color="primary"></v-progress-circular>
                  <div class="mt-2">加载统计数据中...</div>
                </div>
                
                <div v-else-if="auctionStatsError">
                  <v-alert type="error" density="compact">{{ auctionStatsError }}</v-alert>
                </div>
                
                <div v-else>
                  <v-row dense>
                    <v-col cols="6" md="3">
                      <div class="stat-card">
                        <div class="stat-number text-primary">{{ auctionStats.total_auctions || 0 }}</div>
                        <div class="stat-label">总竞拍数</div>
                      </div>
                    </v-col>
                    <v-col cols="6" md="3">
                      <div class="stat-card">
                        <div class="stat-number text-success">{{ auctionStats.active_auctions || 0 }}</div>
                        <div class="stat-label">进行中</div>
                      </div>
                    </v-col>
                    <v-col cols="6" md="3">
                      <div class="stat-card">
                        <div class="stat-number text-warning">{{ auctionStats.total_bids || 0 }}</div>
                        <div class="stat-label">总出价次数</div>
                      </div>
                    </v-col>
                    <v-col cols="6" md="3">
                      <div class="stat-card">
                        <div class="stat-number text-info">{{ auctionStats.total_credits || 0 }}</div>
                        <div class="stat-label">总积分流通</div>
                      </div>
                    </v-col>
                  </v-row>
                </div>
              </v-card-text>
            </v-card>

            <!-- 快速操作卡片 -->
            <v-card class="admin-card-enhanced mb-6">
              <v-card-title class="d-flex align-center">
                <v-icon class="mr-2" color="orange">mdi-lightning-bolt</v-icon>
                <span>快速操作</span>
              </v-card-title>
              
              <v-card-text>
                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-btn
                      @click="showCreateAuctionDialog = true"
                      color="primary"
                      variant="flat"
                      block
                      prepend-icon="mdi-plus"
                    >
                      创建竞拍活动
                    </v-btn>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-btn
                      @click="finishExpiredAuctionsAction"
                      :loading="finishingExpired"
                      color="warning"
                      variant="flat"
                      block
                      prepend-icon="mdi-clock-end"
                    >
                      结束过期竞拍
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- 竞拍活动列表 -->
            <v-card class="admin-card-enhanced">
              <v-card-title class="d-flex align-center">
                <v-icon class="mr-2" color="green">mdi-gavel</v-icon>
                <span>竞拍活动管理</span>
                <v-spacer></v-spacer>
                <v-btn-toggle
                  v-model="auctionStatusFilter"
                  @update:modelValue="filterAuctions"
                  density="compact"
                  variant="outlined"
                >
                  <v-btn value="all" size="small">全部</v-btn>
                  <v-btn value="active" size="small">进行中</v-btn>
                  <v-btn value="ended" size="small">已结束</v-btn>
                </v-btn-toggle>
              </v-card-title>
              
              <v-card-text>
                <div v-if="auctionsLoading" class="text-center py-4">
                  <v-progress-circular indeterminate size="small" color="primary"></v-progress-circular>
                  <div class="mt-2">加载竞拍列表中...</div>
                </div>
                
                <div v-else-if="auctionsError">
                  <v-alert type="error" density="compact">{{ auctionsError }}</v-alert>
                </div>
                
                <div v-else-if="filteredAuctions.length === 0">
                  <v-alert type="info" density="compact">
                    暂无竞拍活动
                  </v-alert>
                </div>
                
                <div v-else>
                  <v-data-table
                    :headers="auctionTableHeaders"
                    :items="filteredAuctions"
                    :loading="auctionsLoading"
                    density="compact"
                    class="auction-table"
                  >
                    <template #item.status="{ item }">
                      <v-chip
                        :color="getAuctionStatusColor(item.status)"
                        size="small"
                        variant="flat"
                      >
                        {{ getAuctionStatusText(item.status) }}
                      </v-chip>
                    </template>
                    
                    <template #item.title="{ item }">
                      <div class="d-flex align-center">
                        <v-icon
                          size="small"
                          class="mr-2"
                          :color="getItemTypeColor(item.item_type)"
                        >
                          {{ getItemTypeIcon(item.item_type) }}
                        </v-icon>
                        <span>{{ item.title || `ID: ${item.id}` || '未知竞拍活动' }}</span>
                      </div>
                    </template>
                    
                    <template #item.starting_price="{ item }">
                      <span class="text-success">{{ item.starting_price || 0 }} 积分</span>
                    </template>
                    
                    <template #item.current_price="{ item }">
                      <span class="text-primary font-weight-bold">
                        {{ item.current_price || item.starting_price || 0 }} 积分
                      </span>
                    </template>
                    
                    <template #item.end_time="{ item }">
                      <div>
                        <div>{{ formatDateTime(item.end_time) }}</div>
                        <div class="text-caption text-medium-emphasis">
                          {{ getTimeStatus(item.end_time) }}
                        </div>
                      </div>
                    </template>
                    
                    <template #item.actions="{ item }">
                      <div class="d-flex gap-2">
                        <v-btn
                          @click="viewAuctionDetails(item)"
                          size="small"
                          variant="text"
                          icon="mdi-eye"
                          color="primary"
                        ></v-btn>
                        <v-btn
                          @click="editAuction(item)"
                          size="small"
                          variant="text"
                          icon="mdi-pencil"
                          color="orange"
                        ></v-btn>
                        <v-btn
                          v-if="item.status === 'active'"
                          @click="finishAuction(item)"
                          size="small"
                          variant="text"
                          icon="mdi-stop"
                          color="warning"
                        ></v-btn>
                        <v-btn
                          @click="deleteAuction(item)"
                          size="small"
                          variant="text"
                          icon="mdi-delete"
                          color="error"
                        ></v-btn>
                      </div>
                    </template>
                  </v-data-table>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- 创建竞拍对话框 -->
    <v-dialog v-model="showCreateAuctionDialog" max-width="600px" persistent>
      <v-card>
        <v-toolbar color="primary" dark flat>
          <v-btn icon dark @click="showCreateAuctionDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>
            <v-icon class="mr-2">mdi-plus</v-icon>
            创建竞拍活动
          </v-toolbar-title>
        </v-toolbar>
        
        <v-card-text class="pa-6">
          <v-form ref="createAuctionForm">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="createAuctionForm.item_name"
                  label="物品名称"
                  :rules="[v => !!v || '物品名称不能为空']"
                  outlined
                  dense
                  required
                ></v-text-field>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-select
                  v-model="createAuctionForm.item_type"
                  :items="[
                    { text: 'Plex 账号', value: 'plex' },
                    { text: 'Emby 账号', value: 'emby' },
                    { text: '积分', value: 'credits' },
                    { text: '邀请码', value: 'invite' }
                  ]"
                  item-title="text"
                  item-value="value"
                  label="物品类型"
                  outlined
                  dense
                  required
                ></v-select>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="createAuctionForm.start_price"
                  label="起拍价格（积分）"
                  :rules="[v => v > 0 || '起拍价格必须大于0']"
                  type="number"
                  outlined
                  dense
                  min="1"
                  required
                ></v-text-field>
              </v-col>
              
              <v-col cols="12">
                <v-text-field
                  v-model.number="createAuctionForm.duration_hours"
                  label="竞拍时长（小时）"
                  :rules="[v => v > 0 || '竞拍时长必须大于0']"
                  type="number"
                  outlined
                  dense
                  min="1"
                  max="168"
                  required
                ></v-text-field>
              </v-col>
              
              <v-col cols="12">
                <v-textarea
                  v-model="createAuctionForm.description"
                  label="物品描述"
                  outlined
                  dense
                  rows="3"
                  placeholder="请描述竞拍物品的详细信息..."
                  hint="如果不填写，将使用默认描述"
                  persistent-hint
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        
        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn @click="showCreateAuctionDialog = false" variant="text">
            取消
          </v-btn>
          <v-btn @click="createNewAuction" color="primary" variant="flat">
            创建竞拍
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 竞拍详情对话框 -->
    <v-dialog v-model="showAuctionDetailDialog" max-width="800px">
      <v-card v-if="selectedAuction">
        <v-toolbar color="info" dark flat>
          <v-btn icon dark @click="closeAuctionDetailDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>
            <v-icon class="mr-2">mdi-eye</v-icon>
            竞拍详情
          </v-toolbar-title>
        </v-toolbar>
        
        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12" md="6">
              <div class="detail-item">
                <div class="text-caption text-medium-emphasis">竞拍ID</div>
                <div class="text-body-1">#{{ selectedAuction.id }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="detail-item">
                <div class="text-caption text-medium-emphasis">状态</div>
                <v-chip
                  :color="getAuctionStatusColor(selectedAuction.status)"
                  size="small"
                  variant="flat"
                >
                  {{ getAuctionStatusText(selectedAuction.status) }}
                </v-chip>
              </div>
            </v-col>
            <v-col cols="12">
              <div class="detail-item">
                <div class="text-caption text-medium-emphasis">物品名称</div>
                <div class="text-h6">{{ selectedAuction.title || `ID: ${selectedAuction.id}` || '未知竞拍活动' }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="detail-item">
                <div class="text-caption text-medium-emphasis">起拍价</div>
                <div class="text-body-1 text-success">{{ selectedAuction.starting_price || 0 }} 积分</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="detail-item">
                <div class="text-caption text-medium-emphasis">当前价格</div>
                <div class="text-body-1 text-primary font-weight-bold">
                  {{ selectedAuction.current_price || selectedAuction.starting_price || 0 }} 积分
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="detail-item">
                <div class="text-caption text-medium-emphasis">结束时间</div>
                <div class="text-body-1">{{ formatDateTime(selectedAuction.end_time) }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="detail-item">
                <div class="text-caption text-medium-emphasis">出价次数</div>
                <div class="text-body-1">{{ auctionBids.length }} 次</div>
              </div>
            </v-col>
          </v-row>
          
          <!-- 出价历史 -->
          <div v-if="auctionBids.length > 0" class="mt-6">
            <div class="text-h6 mb-3">出价历史</div>
            <v-data-table
              :headers="[
                { title: '用户', key: 'bidder_name' },
                { title: '出价金额', key: 'bid_amount' },
                { title: '出价时间', key: 'bid_time' }
              ]"
              :items="auctionBids"
              density="compact"
              :items-per-page="10"
            >
              <template #item.bid_amount="{ item }">
                <span class="text-warning font-weight-bold">{{ item.bid_amount }} 积分</span>
              </template>
              <template #item.bid_time="{ item }">
                {{ formatDateTime(item.bid_time) }}
              </template>
            </v-data-table>
          </div>
          
          <div v-else class="mt-6 text-center">
            <v-alert type="info" density="compact">
              暂无出价记录
            </v-alert>
          </div>
        </v-card-text>
        
        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn @click="closeAuctionDetailDialog" variant="outlined">
            关闭
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 编辑竞拍对话框 -->
    <v-dialog v-model="showEditAuctionDialog" max-width="600px" persistent>
      <v-card v-if="selectedAuction">
        <v-toolbar color="orange" dark flat>
          <v-btn icon dark @click="closeEditAuctionDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>
            <v-icon class="mr-2">mdi-pencil</v-icon>
            编辑竞拍
          </v-toolbar-title>
        </v-toolbar>
        
        <v-card-text class="pa-6">
          <v-form ref="editAuctionForm">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="editAuctionForm.title"
                  label="物品名称"
                  :rules="[v => !!v || '物品名称不能为空']"
                  outlined
                  dense
                  required
                ></v-text-field>
              </v-col>
              
              <v-col cols="12">
                <v-text-field
                  v-model.number="editAuctionForm.starting_price"
                  label="起拍价格（积分）"
                  :rules="[v => v > 0 || '起拍价格必须大于0']"
                  type="number"
                  outlined
                  dense
                  min="1"
                  required
                ></v-text-field>
              </v-col>
              
              <v-col cols="12">
                <v-text-field
                  v-model.number="editAuctionForm.duration_hours"
                  label="延长时长（小时）"
                  :rules="[v => v > 0 || '时长必须大于0']"
                  type="number"
                  outlined
                  dense
                  min="1"
                  max="168"
                  hint="修改此项将从现在开始重新计算结束时间"
                  persistent-hint
                ></v-text-field>
              </v-col>
              
              <v-col cols="12">
                <v-textarea
                  v-model="editAuctionForm.description"
                  label="物品描述"
                  outlined
                  dense
                  rows="3"
                  placeholder="请描述竞拍物品的详细信息..."
                  hint="如果不填写，将使用默认描述"
                  persistent-hint
                ></v-textarea>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        
        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn @click="closeEditAuctionDialog" variant="text">
            取消
          </v-btn>
          <v-btn @click="saveAuctionEdit" color="orange" variant="flat">
            保存修改
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 线路用户详情对话框 -->
    <v-dialog v-model="showLineUsersDialogVisible" max-width="500px">
      <v-card v-if="selectedLineStat">
        <v-toolbar color="deep-purple" dark flat>
          <v-btn icon dark @click="closeLineUsersDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>
            <v-icon class="mr-2">mdi-server-network</v-icon>
            {{ selectedLineStat.line }} - 用户流量排行
          </v-toolbar-title>
        </v-toolbar>
        
        <v-card-text class="pa-4">
          <div class="text-caption text-medium-emphasis mb-3">
            今日流量消耗排行（前{{ selectedLineStat.top_users.length }}名用户）
          </div>
          
          <div class="user-ranking-list">
            <div 
              v-for="(user, index) in selectedLineStat.top_users" 
              :key="user.username"
              class="d-flex align-center justify-space-between py-2"
              :class="{ 'border-b': index < selectedLineStat.top_users.length - 1 }"
            >
              <div class="d-flex align-center">
                <div class="rank-badge mr-3">
                  <v-avatar 
                    :color="getUserRankColor(index)" 
                    size="24"
                  >
                    <span class="text-caption font-weight-bold text-white">
                      {{ index + 1 }}
                    </span>
                  </v-avatar>
                </div>
                <div>
                  <div class="text-body-2 font-weight-medium">{{ user.username }}</div>
                  <div class="text-caption text-medium-emphasis">
                    流量占比: {{ ((user.traffic / selectedLineStat.today_traffic) * 100).toFixed(1) }}%
                  </div>
                </div>
              </div>
              
              <div class="text-right">
                <v-chip 
                  :color="getUserRankColor(index)" 
                  size="small" 
                  variant="tonal"
                  class="font-weight-medium"
                >
                  {{ formatTrafficSize(user.traffic) }}
                </v-chip>
              </div>
            </div>
          </div>
          
          <div class="mt-4 pt-2 border-t">
            <div class="d-flex justify-space-between align-center">
              <span class="text-body-2 font-weight-medium">线路总流量（今日）</span>
              <v-chip color="primary" variant="flat" size="small">
                {{ formatTrafficSize(selectedLineStat.today_traffic) }}
              </v-chip>
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn @click="closeLineUsersDialog" variant="outlined">
            关闭
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 线路流量详情对话框 -->
    <v-dialog v-model="showLineTrafficDialog" max-width="900">
      <v-card>
        <v-card-title class="text-center bg-blue-darken-2 text-white">
          <v-icon start>mdi-lan</v-icon>
          各线路流量详情
        </v-card-title>
        
        <v-card-text class="pa-6">
          <!-- 标签页切换 -->
          <v-tabs v-model="lineTrafficTab" align-tabs="center" color="blue-darken-2" class="mb-4">
            <v-tab value="today">
              <v-icon start>mdi-calendar-today</v-icon>
              今日
            </v-tab>
            <v-tab value="week">
              <v-icon start>mdi-calendar-week</v-icon>
              本周
            </v-tab>
            <v-tab value="month">
              <v-icon start>mdi-calendar-month</v-icon>
              本月
            </v-tab>
          </v-tabs>

          <!-- 标签页内容 -->
          <v-window v-model="lineTrafficTab">
            <!-- 今日流量 -->
            <v-window-item value="today">
              <div v-if="trafficOverview.today.lines.length === 0 && trafficOverview.today.custom_lines.length === 0" class="text-center py-8">
                <v-icon size="64" color="grey-lighten-1">mdi-database-off</v-icon>
                <div class="text-grey-darken-1 mt-2">暂无今日线路流量数据</div>
              </div>
              <div v-else>
                <!-- 常规线路 -->
                <div v-if="trafficOverview.today.lines.length > 0">
                  <div class="text-subtitle-1 mb-3 font-weight-medium">
                    <v-icon size="20" class="mr-1">mdi-server-network</v-icon>
                    常规线路
                  </div>
                  <v-row>
                    <v-col cols="12" md="6" lg="4" v-for="(line, index) in trafficOverview.today.lines" :key="'today-' + index">
                      <v-card class="line-traffic-card" variant="outlined">
                        <v-card-title class="text-center bg-success text-white py-2">
                          <v-icon start>mdi-server</v-icon>
                          {{ line.line }}
                        </v-card-title>
                        <v-card-text class="text-center py-4">
                          <div class="text-h6 text-success">{{ formatTrafficSize(line.traffic) }}</div>
                          <div class="text-caption text-grey-darken-1">今日流量</div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
                
                <!-- 自定义线路 -->
                <div v-if="trafficOverview.today.custom_lines.length > 0" :class="trafficOverview.today.lines.length > 0 ? 'mt-6' : ''">
                  <div class="text-subtitle-1 mb-3 font-weight-medium">
                    <v-icon size="20" class="mr-1" color="purple">mdi-share-variant</v-icon>
                    自定义分享线路
                  </div>
                  <v-row>
                    <v-col cols="12" md="6" lg="4" v-for="(line, index) in trafficOverview.today.custom_lines" :key="'today-custom-' + index">
                      <v-card class="line-traffic-card" variant="outlined">
                        <v-card-title class="text-center bg-purple text-white py-2">
                          <v-icon start>mdi-share-variant</v-icon>
                          {{ line.line }}
                        </v-card-title>
                        <v-card-text class="text-center py-4">
                          <div class="text-h6 text-purple">{{ formatTrafficSize(line.traffic) }}</div>
                          <div class="text-caption text-grey-darken-1">今日流量</div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-window-item>

            <!-- 本周流量 -->
            <v-window-item value="week">
              <div v-if="trafficOverview.week.lines.length === 0 && trafficOverview.week.custom_lines.length === 0" class="text-center py-8">
                <v-icon size="64" color="grey-lighten-1">mdi-database-off</v-icon>
                <div class="text-grey-darken-1 mt-2">暂无本周线路流量数据</div>
              </div>
              <div v-else>
                <!-- 常规线路 -->
                <div v-if="trafficOverview.week.lines.length > 0">
                  <div class="text-subtitle-1 mb-3 font-weight-medium">
                    <v-icon size="20" class="mr-1">mdi-server-network</v-icon>
                    常规线路
                  </div>
                  <v-row>
                    <v-col cols="12" md="6" lg="4" v-for="(line, index) in trafficOverview.week.lines" :key="'week-' + index">
                      <v-card class="line-traffic-card" variant="outlined">
                        <v-card-title class="text-center bg-info text-white py-2">
                          <v-icon start>mdi-server</v-icon>
                          {{ line.line }}
                        </v-card-title>
                        <v-card-text class="text-center py-4">
                          <div class="text-h6 text-info">{{ formatTrafficSize(line.traffic) }}</div>
                          <div class="text-caption text-grey-darken-1">本周流量</div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
                
                <!-- 自定义线路 -->
                <div v-if="trafficOverview.week.custom_lines.length > 0" :class="trafficOverview.week.lines.length > 0 ? 'mt-6' : ''">
                  <div class="text-subtitle-1 mb-3 font-weight-medium">
                    <v-icon size="20" class="mr-1" color="purple">mdi-share-variant</v-icon>
                    自定义分享线路
                  </div>
                  <v-row>
                    <v-col cols="12" md="6" lg="4" v-for="(line, index) in trafficOverview.week.custom_lines" :key="'week-custom-' + index">
                      <v-card class="line-traffic-card" variant="outlined">
                        <v-card-title class="text-center bg-purple text-white py-2">
                          <v-icon start>mdi-share-variant</v-icon>
                          {{ line.line }}
                        </v-card-title>
                        <v-card-text class="text-center py-4">
                          <div class="text-h6 text-purple">{{ formatTrafficSize(line.traffic) }}</div>
                          <div class="text-caption text-grey-darken-1">本周流量</div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-window-item>

            <!-- 本月流量 -->
            <v-window-item value="month">
              <div v-if="trafficOverview.month.lines.length === 0 && trafficOverview.month.custom_lines.length === 0" class="text-center py-8">
                <v-icon size="64" color="grey-lighten-1">mdi-database-off</v-icon>
                <div class="text-grey-darken-1 mt-2">暂无本月线路流量数据</div>
              </div>
              <div v-else>
                <!-- 常规线路 -->
                <div v-if="trafficOverview.month.lines.length > 0">
                  <div class="text-subtitle-1 mb-3 font-weight-medium">
                    <v-icon size="20" class="mr-1">mdi-server-network</v-icon>
                    常规线路
                  </div>
                  <v-row>
                    <v-col cols="12" md="6" lg="4" v-for="(line, index) in trafficOverview.month.lines" :key="'month-' + index">
                      <v-card class="line-traffic-card" variant="outlined">
                        <v-card-title class="text-center bg-deep-purple text-white py-2">
                          <v-icon start>mdi-server</v-icon>
                          {{ line.line }}
                        </v-card-title>
                        <v-card-text class="text-center py-4">
                          <div class="text-h6 text-deep-purple">{{ formatTrafficSize(line.traffic) }}</div>
                          <div class="text-caption text-grey-darken-1">本月流量</div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
                
                <!-- 自定义线路 -->
                <div v-if="trafficOverview.month.custom_lines.length > 0" :class="trafficOverview.month.lines.length > 0 ? 'mt-6' : ''">
                  <div class="text-subtitle-1 mb-3 font-weight-medium">
                    <v-icon size="20" class="mr-1" color="purple">mdi-share-variant</v-icon>
                    自定义分享线路
                  </div>
                  <v-row>
                    <v-col cols="12" md="6" lg="4" v-for="(line, index) in trafficOverview.month.custom_lines" :key="'month-custom-' + index">
                      <v-card class="line-traffic-card" variant="outlined">
                        <v-card-title class="text-center bg-purple text-white py-2">
                          <v-icon start>mdi-share-variant</v-icon>
                          {{ line.line }}
                        </v-card-title>
                        <v-card-text class="text-center py-4">
                          <div class="text-h6 text-purple">{{ formatTrafficSize(line.traffic) }}</div>
                          <div class="text-caption text-grey-darken-1">本月流量</div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-window-item>
          </v-window>
        </v-card-text>
        
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn @click="showLineTrafficDialog = false" variant="outlined">
            关闭
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 勋章编辑对话框 -->
    <badge-editor-dialog
      v-model="showBadgeEditorDialog"
      :badge="editingBadge"
      @badge-saved="handleBadgeSaved"
    />
  </div>
</template>

<script>
import { getUserInfo, getSystemStats, getPremiumStatistics } from '@/api'
import DonationDialog from '@/components/DonationDialog.vue'
import DonationRegistrationManagementDialog from '@/components/DonationRegistrationManagementDialog.vue'
import AdminInviteCodeDialog from '@/components/AdminInviteCodeDialog.vue'
import LineManagementDialog from '@/components/LineManagementDialog.vue'
import WheelAdminPanel from '@/components/WheelAdminPanel.vue'
import BlackjackAdminPanel from '@/components/BlackjackAdminPanel.vue'
import BlackjackTournamentAdminPanel from '@/components/BlackjackTournamentAdminPanel.vue'
import GiftPackAdminPanel from '@/components/GiftPackAdminPanel.vue'
import BadgeEditorDialog from '@/components/BadgeEditorDialog.vue'
import CustomLineManagement from '@/components/CustomLineManagement.vue'
import {
  getAdminSettings,
  setPlexRegister,
  setEmbyRegister,
  setPremiumFree,
  setFreePremiumLines,
  setInvitationCredits,
  setUnlockCredits,
  setPremiumDailyCredits,
  setUserTrafficLimit,
  setPremiumUserTrafficLimit,
  setPremiumUnlockEnabled,
  setCreditsTransferEnabled,
  setLineScheduleUnlockCredits,
  setDownloadUnlockCredits,
  setCreditsCostPer10Gb,
  setNsfwLibs,
  setDonationMultiplier,
  setUpayCryptoTypes,
  setVaultwardenEnabled,
  setVaultwardenRedeemCredits
} from '@/services/adminService.js'
import { getWheelStats } from '@/services/wheelService.js'
import { getBlackjackConfig, getBlackjackAdminStats } from '@/services/blackjackService.js'
import { getAuctionStats, getAllAuctions, finishExpiredAuctions, finishAuction, deleteAuction, createAuction, getAuctionBids, updateAuction } from '@/services/auctionService.js'
import { listTreasureIssues, createTreasureIssue, cancelTreasureIssue } from '@/services/treasureService.js'
import { listPredictionMarkets, createPredictionMarket, closePredictionMarket, resolvePredictionMarket } from '@/services/predictionService.js'
import { adminListGiftPacks } from '@/services/giftPackService.js'
import { getPremiumLineTrafficStats, formatTrafficSize, formatUsername, getTrafficOverview } from '@/services/trafficService.js'
import { getAllCryptoDonationOrdersAdmin, ORDER_STATUS } from '@/services/cryptoDonationService.js'
import { getBadgeCenterConfig, updateBadgeConfig, adminGetAllBadges, adminDeleteBadge } from '@/services/badgeService.js'

const BYTES_PER_GB = 1024 * 1024 * 1024

export default {
  name: 'Management',
  components: {
    DonationDialog,
    DonationRegistrationManagementDialog,
    AdminInviteCodeDialog,
    LineManagementDialog,
    WheelAdminPanel,
    BlackjackAdminPanel,
    BlackjackTournamentAdminPanel,
    GiftPackAdminPanel,
    BadgeEditorDialog,
    CustomLineManagement
  },
  data() {
    return {
      loading: true,
      error: null,
      isAdmin: false,
      currentTab: 'overview', // 默认显示概览tab
      adminSettings: {
        plex_register: false,
        emby_register: false,
        lines: [],
        premium_free: false,
        premium_lines: [],
        free_premium_lines: [],
        premium_unlock_enabled: false,
        credits_transfer_enabled: true,
        invitation_credits: 288,
        unlock_credits: 100,
        premium_daily_credits: 15,
        user_traffic_limit_gb: 12,
        premium_user_traffic_limit_gb: 24,
        line_schedule_unlock_credits: 264,
        download_unlock_credits: 368,
        credits_cost_per_10gb: 5,
        nsfw_libs: [],
        donation_multiplier: 5,
        upay_crypto_types: [],
        vaultwarden_enabled: false,
        vaultwarden_redeem_credits: 500,
        loaded: false // 添加标记，避免重复加载
      },
      adminLoading: false,
      adminError: null,
      wheelStats: {
        totalSpins: 0,
        activeUsers: 0,
        todaySpins: 0,
        lastWeekSpins: 0,
        totalCreditsChange: 0.0,
        totalCostCredits: 0,
        totalInviteCodes: 0
      },
      auctionStats: {
        total_auctions: 0,
        active_auctions: 0,
        total_bids: 0,
        total_value: 0.0
      },
      treasureStats: {
        total_issues: 0,
        active_issues: 0
      },
      predictionStats: {
        total_markets: 0,
        active_markets: 0
      },
      giftPackStats: {
        total_packs: 0,
        active_packs: 0,
        upcoming_packs: 0,
        total_claims: 0
      },
      // 21 点运营统计。金额项只统计已结束的手牌，进行中的押注不计入
      blackjackStats: {
        total_hands: 0,
        active_hands: 0,
        total_players: 0,
        today_hands: 0,
        total_wagered: 0,
        total_rake: 0,
        net_credits: 0,
        jackpot_paid: 0,
        jackpot_balance: 0
      },
      // 竞拍管理相关数据
      auctionStatsLoading: false,
      auctionStatsError: null,
      auctionsLoading: false,
      auctionsError: null,
      auctions: [],
      filteredAuctions: [],
      auctionStatusFilter: 'all',
      finishingExpired: false,
      showCreateAuctionDialog: false,
      showAuctionDetailDialog: false,
      showEditAuctionDialog: false,
      selectedAuction: null,
      auctionBids: [],
      createAuctionForm: {
        item_name: '',
        item_type: 'plex',
        start_price: 100,
        duration_hours: 72,
        description: ''
      },
      editAuctionForm: {
        title: '',
        description: '',
        starting_price: 100,
        duration_hours: 72
      },
      auctionTableHeaders: [
        { title: 'ID', key: 'id', sortable: true, width: 80 },
        { title: '物品', key: 'title', sortable: true },
        { title: '状态', key: 'status', sortable: true, width: 100 },
        { title: '起拍价', key: 'starting_price', sortable: true, width: 120 },
        { title: '当前价', key: 'current_price', sortable: true, width: 120 },
        { title: '结束时间', key: 'end_time', sortable: true, width: 180 },
        { title: '操作', key: 'actions', sortable: false, width: 160 }
      ],
      showWheelManagement: false,
      showBlackjackManagement: false,
      blackjackTab: 'cash', // 21 点管理弹窗内的现金局 / 锦标赛切换
      blackjackEnabled: false,
      showAuctionManagement: false,
      showTreasureManagement: false,
      showPredictionManagement: false,
      showGiftPackManagement: false,
      treasureIssuesLoading: false,
      treasureIssuesError: null,
      deletingTreasureIssueId: null,
      treasureIssues: [],
      createTreasureForm: {
        title: '',
        description: '',
        prize_credits: 100,
        total_credits_required: 1200,
        credits_per_share: 10,
        start_number: ''
      },
      predictionMarketsLoading: false,
      predictionMarketsError: null,
      predictionMarkets: [],
      closingPredictionMarketId: null,
      resolvingPredictionMarketId: null,
      createPredictionForm: {
        title: '',
        description: '',
        betting_deadline: '',
        virtual_yes_pool: 500,
        virtual_no_pool: 500,
        max_bet_per_user: 500
      },
      systemStats: {
        plex_users: 0,
        emby_users: 0,
        total_users: 0,
        nsfw_unlocked_users: 0,
        line_schedule_unlocked_users: 0,
        vaultwarden_redeemed_count: 0,
        download_unlocked_users: 0
      },
      systemStatsLoading: false,
      systemStatsError: null,
      premiumStats: {
        total_premium_users: 0,
        active_premium_users: 0,
        premium_plex_users: 0,
        premium_emby_users: 0,
      },
      premiumStatsLoading: false,
      premiumStatsError: null,
      // Premium 线路流量统计相关数据
      trafficStats: [],
      trafficStatsLoading: false,
      trafficStatsError: null,
      showLineUsersDialogVisible: false,
      selectedLineStat: null,
      // 流量概览统计数据
      trafficOverview: {
        today: { total: 0, emby: 0, plex: 0, lines: [], custom_lines: [] },
        week: { total: 0, emby: 0, plex: 0, lines: [], custom_lines: [] },
        month: { total: 0, emby: 0, plex: 0, lines: [], custom_lines: [] }
      },
      trafficOverviewLoading: false,
      trafficOverviewError: null,
      // 线路流量详情对话框
      showLineTrafficDialog: false,
      lineTrafficTab: 'today',
      // Crypto 捐赠管理对话框
      showCryptoDonationDialog: false,
      cryptoDonationOrders: [],
      cryptoDonationLoading: false,
      cryptoDonationError: null,
      cryptoDonationPage: 1,
      cryptoDonationPerPage: 20,
      cryptoDonationTotal: 0,
      cryptoDonationStatusFilter: null,
      ORDER_STATUS,
      // 勋章管理相关数据
      badgeConfig: {
        enabled: false,
        message: ''
      },
      badges: [],
      badgesLoading: false,
      badgesError: null,
      showBadgeEditorDialog: false,
      editingBadge: null
    }
  },
  mounted() {
    this.checkUserStatus()
  },
  watch: {
    // 监听tab切换
    currentTab(newTab) {
      console.log('标签页切换到:', newTab, '是否为管理员:', this.isAdmin)
      // 如果切换到概览tab，则并发获取系统统计数据
      if (newTab === 'overview') {
        Promise.all([
          this.fetchSystemStats(),
          this.fetchPremiumStats(),
          this.fetchTrafficStats(),
          this.fetchTrafficOverview()
        ])
      }
      // 如果切换到设置项tab且是管理员，则获取管理员设置
      if (newTab === 'settings' && this.isAdmin && !this.adminSettings.loaded) {
        this.fetchAdminSettings()
        this.fetchBadgeData()
      }
      // 如果切换到活动管理tab且是管理员，则并发加载活动统计数据
      if (newTab === 'wheel' && this.isAdmin) {
        console.log('切换到活动管理标签页，开始加载活动数据...')
        Promise.all([
          this.loadWheelStats(),
          this.loadAuctionStats(),
          this.loadTreasureStats(),
          this.loadPredictionStats(),
          this.loadGiftPackStats(),
          this.loadBlackjackStatus(),
          this.loadBlackjackStats()
        ])
      }
    }
  },
  methods: {
    async checkUserStatus() {
      try {
        this.loading = true
        // 获取用户信息来检查管理员权限
        const response = await getUserInfo()
        this.isAdmin = response.data.is_admin
        
        // 如果当前在概览tab，则并发获取系统统计数据
        if (this.currentTab === 'overview') {
          await Promise.all([
            this.fetchSystemStats(),
            this.fetchPremiumStats(),
            this.fetchTrafficStats(),
            this.fetchTrafficOverview()
          ])
        }
        // 如果是管理员且当前在设置项tab，则获取管理员设置
        if (this.isAdmin && this.currentTab === 'settings') {
          await this.fetchAdminSettings()
          await this.fetchBadgeData()
        }
        // 如果是管理员且当前在活动管理tab，则并发加载活动统计数据
        if (this.isAdmin && this.currentTab === 'wheel') {
          await Promise.all([
            this.loadWheelStats(),
            this.loadAuctionStats(),
            this.loadTreasureStats(),
            this.loadPredictionStats(),
            this.loadGiftPackStats(),
            this.loadBlackjackStatus()
          ])
        }
        this.loading = false
      } catch (err) {
        this.error = err.response?.data?.detail || '检查用户状态失败'
        this.loading = false
        console.error('检查用户状态失败:', err)
      }
    },
    
    async fetchAdminSettings() {
      try {
        this.adminLoading = true
        this.adminError = null
        const response = await getAdminSettings()
        this.adminSettings = {
          ...response.data,
          user_traffic_limit_gb: this.bytesToGb(response.data.user_traffic_limit),
          premium_user_traffic_limit_gb: this.bytesToGb(response.data.premium_user_traffic_limit),
          loaded: true
        }
        this.adminLoading = false
      } catch (err) {
        this.adminError = err.response?.data?.detail || '获取管理员设置失败'
        this.adminLoading = false
        console.error('获取管理员设置失败:', err)
      }
    },

    bytesToGb(value) {
      return Math.round((Number(value || 0) / BYTES_PER_GB) * 100) / 100
    },

    gbToBytes(value) {
      return Math.round(Number(value) * BYTES_PER_GB)
    },
    
    async fetchSystemStats() {
      try {
        this.systemStatsLoading = true
        this.systemStatsError = null
        const response = await getSystemStats()
        this.systemStats = response.data
        this.systemStatsLoading = false
      } catch (err) {
        this.systemStatsError = err.response?.data?.detail || '获取系统统计失败'
        this.systemStatsLoading = false
        console.error('获取系统统计失败:', err)
      }
    },
    
    async fetchPremiumStats() {
      try {
        this.premiumStatsLoading = true
        this.premiumStatsError = null
        const response = await getPremiumStatistics()
        this.premiumStats = response.data
        this.premiumStatsLoading = false
      } catch (err) {
        this.premiumStatsError = err.response?.data?.detail || '获取Premium统计失败'
        this.premiumStatsLoading = false
        console.error('获取Premium统计失败:', err)
      }
    },
    
    async refreshOverviewStats() {
      await Promise.all([
        this.fetchSystemStats(),
        this.fetchPremiumStats(),
        this.fetchTrafficStats(),
        this.fetchTrafficOverview()
      ])
    },

    // =====================
    // Prediction 管理
    // =====================
    async loadPredictionStats() {
      try {
        this.predictionMarketsLoading = true
        this.predictionMarketsError = null
        const res = await listPredictionMarkets({ include_closed: true, limit: 200 })
        this.predictionMarkets = res.data.markets || []
        this.predictionStats.total_markets = this.predictionMarkets.length
        this.predictionStats.active_markets = this.predictionMarkets.filter(i => i.status === 1).length
      } catch (err) {
        this.predictionMarketsError = err.response?.data?.detail || '加载预测题目失败'
        console.error('加载预测题目失败:', err)
      } finally {
        this.predictionMarketsLoading = false
      }
    },

    openPredictionManagement() {
      this.showPredictionManagement = true
      this.loadPredictionStats()
    },

    closePredictionManagement() {
      this.showPredictionManagement = false
      this.predictionMarketsError = null
    },

    // =====================
    // Gift Pack 管理
    // =====================
    async loadGiftPackStats() {
      try {
        const res = await adminListGiftPacks({ page: 1, page_size: 200 })
        const packs = res.data.packs || []
        this.giftPackStats.total_packs = packs.length
        this.giftPackStats.active_packs = packs.filter(
          p => p.lifecycle === 'active' && p.is_enabled
        ).length
        this.giftPackStats.upcoming_packs = packs.filter(p => p.lifecycle === 'upcoming').length
        this.giftPackStats.total_claims = packs.reduce(
          (sum, p) => sum + (p.claimed_count || 0), 0
        )
      } catch (err) {
        console.error('加载礼包统计失败:', err)
      }
    },

    openGiftPackManagement() {
      this.showGiftPackManagement = true
      this.loadGiftPackStats()
    },

    closeGiftPackManagement() {
      this.showGiftPackManagement = false
      // 关闭时同步入口卡的统计，避免面板内的增删改与卡片数字不一致
      this.loadGiftPackStats()
    },

    refreshGiftPackPanel() {
      this.$refs.giftPackAdminPanel?.loadPacks()
      this.loadGiftPackStats()
    },

    formatDateTimeByTs(ts) {
      if (!ts) return '未设置'
      try {
        return new Date(Number(ts) * 1000).toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        })
      } catch (err) {
        return String(ts)
      }
    },

    async submitCreatePredictionMarket() {
      try {
        const title = String(this.createPredictionForm.title || '').trim()
        if (!title) {
          this.showMessage('请输入题目标题', 'error')
          return
        }
        if (!this.createPredictionForm.betting_deadline) {
          this.showMessage('请设置押注截止时间', 'error')
          return
        }

        const deadlineSec = Math.floor(new Date(this.createPredictionForm.betting_deadline).getTime() / 1000)
        if (!Number.isFinite(deadlineSec) || deadlineSec <= Math.floor(Date.now() / 1000)) {
          this.showMessage('截止时间必须晚于当前时间', 'error')
          return
        }

        const payload = {
          title,
          description: this.createPredictionForm.description || null,
          betting_deadline: deadlineSec,
          virtual_yes_pool: Number(this.createPredictionForm.virtual_yes_pool),
          virtual_no_pool: Number(this.createPredictionForm.virtual_no_pool),
          max_bet_per_user: Number(this.createPredictionForm.max_bet_per_user)
        }

        await createPredictionMarket(payload)
        this.showMessage('预测题目创建成功', 'success')
        this.createPredictionForm = {
          title: '',
          description: '',
          betting_deadline: '',
          virtual_yes_pool: 500,
          virtual_no_pool: 500,
          max_bet_per_user: 500
        }
        await this.loadPredictionStats()
      } catch (err) {
        const msg = err.response?.data?.detail || '创建预测题目失败'
        this.showMessage(msg, 'error')
        console.error('创建预测题目失败:', err)
      }
    },

    async submitClosePredictionMarket(item) {
      try {
        const id = item?.id
        if (!id) return
        this.closingPredictionMarketId = id
        await closePredictionMarket(id)
        this.showMessage(`题目 #${id} 已截止押注`, 'success')
        await this.loadPredictionStats()
      } catch (err) {
        const msg = err.response?.data?.detail || '截止押注失败'
        this.showMessage(msg, 'error')
        console.error('截止押注失败:', err)
      } finally {
        this.closingPredictionMarketId = null
      }
    },

    async submitResolvePredictionMarket(item, resultOption) {
      try {
        const id = item?.id
        if (!id) return
        this.resolvingPredictionMarketId = id
        await resolvePredictionMarket(id, {
          result_option: Number(resultOption),
          resolution_note: resultOption === 1 ? '管理员裁决：YES' : '管理员裁决：NO'
        })
        this.showMessage(`题目 #${id} 已完成裁决`, 'success')
        await this.loadPredictionStats()
      } catch (err) {
        const msg = err.response?.data?.detail || '裁决失败'
        this.showMessage(msg, 'error')
        console.error('裁决失败:', err)
      } finally {
        this.resolvingPredictionMarketId = null
      }
    },

    // =====================
    // Treasure 管理
    // =====================
    async loadTreasureStats() {
      try {
        this.treasureIssuesLoading = true
        this.treasureIssuesError = null
        const res = await listTreasureIssues({ include_closed: true, limit: 200 })
        this.treasureIssues = res.data.issues || []
        this.treasureStats.total_issues = this.treasureIssues.length
        this.treasureStats.active_issues = this.treasureIssues.filter(i => i.status === 1).length
      } catch (err) {
        this.treasureIssuesError = err.response?.data?.detail || '加载夺宝期数失败'
        console.error('加载夺宝期数失败:', err)
      } finally {
        this.treasureIssuesLoading = false
      }
    },

    openTreasureManagement() {
      this.showTreasureManagement = true
      this.loadTreasureStats()
    },

    closeTreasureManagement() {
      this.showTreasureManagement = false
      this.treasureIssuesError = null
    },

    async submitCreateTreasureIssue() {
      try {
        const title = String(this.createTreasureForm.title || '').trim()
        const payload = {
          ...(title ? { title } : {}),
          description: this.createTreasureForm.description || null,
          prize_credits: Number(this.createTreasureForm.prize_credits),
          total_credits_required: Number(this.createTreasureForm.total_credits_required),
          credits_per_share: Number(this.createTreasureForm.credits_per_share),
        }

        const rawStartNumber = this.createTreasureForm.start_number
        if (rawStartNumber !== '' && rawStartNumber !== null && rawStartNumber !== undefined) {
          const n = Number(rawStartNumber)
          if (Number.isFinite(n) && n > 0) {
            payload.start_number = n
          }
        }
        await createTreasureIssue(payload)
        this.showMessage('夺宝期数创建成功', 'success')
        await this.loadTreasureStats()
      } catch (err) {
        const msg = err.response?.data?.detail || '创建夺宝期数失败'
        this.showMessage(msg, 'error')
        console.error('创建夺宝期数失败:', err)
      }
    },

    async confirmCancelTreasureIssue(issue) {
      try {
        const id = issue?.id
        if (!id) return

        this.deletingTreasureIssueId = id

        const tg = window.Telegram?.WebApp
  const message = `确认取消进行中的夺宝期数 #${id} 吗？\n\n将会：\n- 取消本期\n- 删除全部参与记录\n- 自动退还参与者已消耗积分`

        let ok = false
        if (tg?.showConfirm) {
          ok = await new Promise((resolve) => {
            tg.showConfirm(message, (confirmed) => resolve(Boolean(confirmed)))
          })
        } else {
          ok = window.confirm(message)
        }
        if (!ok) return

        const res = await cancelTreasureIssue(id)
        const data = res?.data || {}
        const refundedTotal = data.refunded_total ?? 0
        const refundedUsers = data.refunded_users ?? 0
  this.showMessage(`期数 #${id} 已取消，已退还 ${refundedTotal} 积分（${refundedUsers} 人）`, 'success')

        // 本地立即更新 UI（减少等待感），再刷新一次确保与服务端一致
        const idx = this.treasureIssues.findIndex((x) => x.id === id)
        if (idx >= 0) {
          const old = this.treasureIssues[idx]
          this.treasureIssues.splice(idx, 1, {
            ...old,
            status: 3,
            shares_sold: 0,
            winner_number: null,
            winner_tg_id: null
          })
        }
        await this.loadTreasureStats()
      } catch (err) {
        const msg = err.response?.data?.detail || '取消夺宝期数失败'
        this.showMessage(msg, 'error')
        console.error('取消夺宝期数失败:', err)
      } finally {
        this.deletingTreasureIssueId = null
      }
    },
    
    async updateCreditsCostPer10Gb() {
      const value = Number(this.adminSettings.credits_cost_per_10gb)
      if (!Number.isInteger(value) || value < 0) {
        this.showMessage('积分值必须是非负整数', 'error')
        await this.fetchAdminSettings()
        return
      }
      try {
        await setCreditsCostPer10Gb(value)
        this.showMessage('每 10GB 流量扣费设置已更新')
      } catch (err) {
        await this.fetchAdminSettings()
        this.showMessage('更新每 10GB 流量扣费失败', 'error')
      }
    },

    async updateNsfwLibs() {
      const previous = [...this.adminSettings.nsfw_libs]
      try {
        await setNsfwLibs(this.adminSettings.nsfw_libs)
        this.showMessage('NSFW 媒体库设置已更新')
      } catch (err) {
        this.adminSettings.nsfw_libs = previous
        await this.fetchAdminSettings()
        this.showMessage('更新 NSFW 媒体库失败', 'error')
      }
    },

    async updateDonationMultiplier() {
      const value = Number(this.adminSettings.donation_multiplier)
      if (!Number.isInteger(value) || value < 0) {
        this.showMessage('倍率必须是非负整数', 'error')
        await this.fetchAdminSettings()
        return
      }
      try {
        await setDonationMultiplier(value)
        this.showMessage('捐赠积分倍率已更新')
      } catch (err) {
        await this.fetchAdminSettings()
        this.showMessage('更新捐赠积分倍率失败', 'error')
      }
    },

    async updateUpayCryptoTypes() {
      const previous = [...this.adminSettings.upay_crypto_types]
      try {
        await setUpayCryptoTypes(this.adminSettings.upay_crypto_types)
        this.showMessage('UPAY 加密货币类型已更新')
      } catch (err) {
        this.adminSettings.upay_crypto_types = previous
        await this.fetchAdminSettings()
        this.showMessage('更新 UPAY 加密货币类型失败', 'error')
      }
    },

    async updateVaultwardenEnabled() {
      try {
        await setVaultwardenEnabled(this.adminSettings.vaultwarden_enabled)
        this.showMessage('Vaultwarden 开关已更新')
      } catch (err) {
        this.adminSettings.vaultwarden_enabled = !this.adminSettings.vaultwarden_enabled
        await this.fetchAdminSettings()
        this.showMessage('更新 Vaultwarden 开关失败', 'error')
      }
    },

    async updateVaultwardenRedeemCredits() {
      const value = Number(this.adminSettings.vaultwarden_redeem_credits)
      if (!Number.isInteger(value) || value < 0) {
        this.showMessage('积分值必须是非负整数', 'error')
        await this.fetchAdminSettings()
        return
      }
      try {
        await setVaultwardenRedeemCredits(value)
        this.showMessage('Vaultwarden 兑换积分已更新')
      } catch (err) {
        await this.fetchAdminSettings()
        this.showMessage('更新 Vaultwarden 兑换积分失败', 'error')
      }
    },

    async updatePlexRegister() {
      try {
        await setPlexRegister(this.adminSettings.plex_register)
        this.showMessage('Plex 注册设置已更新')
      } catch (err) {
        // 回滚状态
        this.adminSettings.plex_register = !this.adminSettings.plex_register
        this.showMessage('更新 Plex 注册设置失败', 'error')
        console.error('更新 Plex 注册设置失败:', err)
      }
    },
    
    async updateEmbyRegister() {
      try {
        await setEmbyRegister(this.adminSettings.emby_register)
        this.showMessage('Emby 注册设置已更新')
      } catch (err) {
        // 回滚状态
        this.adminSettings.emby_register = !this.adminSettings.emby_register
        this.showMessage('更新 Emby 注册设置失败', 'error')
        console.error('更新 Emby 注册设置失败:', err)
      }
    },
    
    async updatePremiumFree() {
      try {
        await setPremiumFree(this.adminSettings.premium_free)
        this.showMessage('高级线路免费使用设置已更新')
      } catch (err) {
        // 回滚状态
        this.adminSettings.premium_free = !this.adminSettings.premium_free
        this.showMessage('更新高级线路免费开放设置失败', 'error')
        console.error('更新高级线路免费开放设置失败:', err)
      }
    },
    
    async updatePremiumUnlockEnabled() {
      try {
        await setPremiumUnlockEnabled(this.adminSettings.premium_unlock_enabled)
        this.showMessage('Premium 解锁开放设置已更新')
      } catch (err) {
        // 回滚状态
        this.adminSettings.premium_unlock_enabled = !this.adminSettings.premium_unlock_enabled
        this.showMessage('更新 Premium 解锁开放设置失败', 'error')
        console.error('更新 Premium 解锁开放设置失败:', err)
      }
    },
    
    async updateCreditsTransferEnabled() {
      try {
        await setCreditsTransferEnabled(this.adminSettings.credits_transfer_enabled)
        this.showMessage('积分转移功能设置已更新')
      } catch (err) {
        // 回滚状态
        this.adminSettings.credits_transfer_enabled = !this.adminSettings.credits_transfer_enabled
        this.showMessage('更新积分转移功能设置失败', 'error')
        console.error('更新积分转移功能设置失败:', err)
      }
    },
    
    async updateFreePremiumLines() {
      try {
        await setFreePremiumLines(this.adminSettings.free_premium_lines)
        this.showMessage(`免费高级线路设置已更新，共 ${this.adminSettings.free_premium_lines.length} 条线路`)
      } catch (err) {
        this.showMessage('更新免费高级线路设置失败', 'error')
        console.error('更新免费高级线路设置失败:', err)
        // 重新获取设置以恢复状态
        await this.fetchAdminSettings()
      }
    },
    
    async updateInvitationCredits() {
      try {
        const credits = parseInt(this.adminSettings.invitation_credits)
        if (isNaN(credits) || credits < 0) {
          this.showMessage('积分值必须是正整数', 'error')
          // 重新获取设置以恢复状态
          await this.fetchAdminSettings()
          return
        }
        await setInvitationCredits(credits)
        this.showMessage(`邀请码生成所需积分已设置为 ${credits}`)
      } catch (err) {
        this.showMessage('更新邀请码积分设置失败', 'error')
        console.error('更新邀请码积分设置失败:', err)
        // 重新获取设置以恢复状态
        await this.fetchAdminSettings()
      }
    },
    
    async updateUnlockCredits() {
      try {
        const credits = parseInt(this.adminSettings.unlock_credits)
        if (isNaN(credits) || credits < 0) {
          this.showMessage('积分值必须是正整数', 'error')
          // 重新获取设置以恢复状态
          await this.fetchAdminSettings()
          return
        }
        await setUnlockCredits(credits)
        this.showMessage(`解锁 NSFW 所需积分已设置为 ${credits}`)
      } catch (err) {
        this.showMessage('更新解锁积分设置失败', 'error')
        console.error('更新解锁积分设置失败:', err)
        // 重新获取设置以恢复状态
        await this.fetchAdminSettings()
      }
    },
    
    async updatePremiumDailyCredits() {
      try {
        const credits = parseInt(this.adminSettings.premium_daily_credits)
        if (isNaN(credits) || credits < 0) {
          this.showMessage('积分值必须是正整数', 'error')
          // 重新获取设置以恢复状态
          await this.fetchAdminSettings()
          return
        }
        await setPremiumDailyCredits(credits)
        this.showMessage(`解锁 Premium 每日所需积分已设置为 ${credits}`)
      } catch (err) {
        this.showMessage('更新 Premium 每日积分设置失败', 'error')
        console.error('更新 Premium 每日积分设置失败:', err)
        // 重新获取设置以恢复状态
        await this.fetchAdminSettings()
      }
    },

    async updateUserTrafficLimit() {
      try {
        const trafficLimitGb = Number(this.adminSettings.user_traffic_limit_gb)
        if (!Number.isFinite(trafficLimitGb) || trafficLimitGb < 0) {
          this.showMessage('流量额度必须是非负数字', 'error')
          // 重新获取设置以恢复状态
          await this.fetchAdminSettings()
          return
        }
        await setUserTrafficLimit(this.gbToBytes(trafficLimitGb))
        this.showMessage(`普通用户每日免费 Premium 流量额度已设置为 ${trafficLimitGb}GB`)
      } catch (err) {
        this.showMessage('更新普通用户每日免费流量额度失败', 'error')
        console.error('更新普通用户每日免费流量额度失败:', err)
        // 重新获取设置以恢复状态
        await this.fetchAdminSettings()
      }
    },

    async updatePremiumUserTrafficLimit() {
      try {
        const trafficLimitGb = Number(this.adminSettings.premium_user_traffic_limit_gb)
        if (!Number.isFinite(trafficLimitGb) || trafficLimitGb < 0) {
          this.showMessage('流量额度必须是非负数字', 'error')
          // 重新获取设置以恢复状态
          await this.fetchAdminSettings()
          return
        }
        await setPremiumUserTrafficLimit(this.gbToBytes(trafficLimitGb))
        this.showMessage(`高级用户每日免费 Premium 流量额度已设置为 ${trafficLimitGb}GB`)
      } catch (err) {
        this.showMessage('更新高级用户每日免费流量额度失败', 'error')
        console.error('更新高级用户每日免费流量额度失败:', err)
        // 重新获取设置以恢复状态
        await this.fetchAdminSettings()
      }
    },
    
    async updateLineScheduleUnlockCredits() {
      try {
        const credits = parseInt(this.adminSettings.line_schedule_unlock_credits)
        if (isNaN(credits) || credits < 0) {
          this.showMessage('积分值必须是正整数', 'error')
          // 重新获取设置以恢复状态
          await this.fetchAdminSettings()
          return
        }
        await setLineScheduleUnlockCredits(credits)
        this.showMessage(`解锁线路调度所需积分已设置为 ${credits}`)
      } catch (err) {
        this.showMessage('更新线路调度解锁积分设置失败', 'error')
        console.error('更新线路调度解锁积分设置失败:', err)
        // 重新获取设置以恢复状态
        await this.fetchAdminSettings()
      }
    },
    
    async updateDownloadUnlockCredits() {
      try {
        const credits = parseInt(this.adminSettings.download_unlock_credits)
        if (isNaN(credits) || credits < 0) {
          this.showMessage('积分值必须是正整数', 'error')
          // 重新获取设置以恢复状态
          await this.fetchAdminSettings()
          return
        }
        await setDownloadUnlockCredits(credits)
        this.showMessage(`解锁下载/同步所需积分已设置为 ${credits}`)
      } catch (err) {
        this.showMessage('更新下载解锁积分设置失败', 'error')
        console.error('更新下载解锁积分设置失败:', err)
        // 重新获取设置以恢复状态
        await this.fetchAdminSettings()
      }
    },
    
    removeFreeLine(line) {
      const index = this.adminSettings.free_premium_lines.indexOf(line)
      if (index > -1) {
        this.adminSettings.free_premium_lines.splice(index, 1)
        this.updateFreePremiumLines()
      }
    },
    
    showMessage(message, type = 'success') {
      if (window.Telegram?.WebApp) {
        window.Telegram.WebApp.showPopup({
          title: type === 'error' ? '错误' : '成功',
          message: message
        })
      } else {
        alert(message)
      }
    },
    
    // 打开捐赠对话框
    openDonationDialog() {
      this.$refs.donationDialog.open();
    },
    
    // 打开自助捐助登记管理对话框
    openDonationRegistrationDialog() {
      this.$refs.donationRegistrationDialog.open();
    },
    
    // 打开邀请码管理对话框
    openInviteCodeDialog() {
      this.$refs.inviteCodeDialog.open();
    },
    
    // 处理邀请码生成完成事件
    handleInviteCodesGenerated(data) {
      console.log('邀请码生成完成:', data);
      // 可以在这里添加额外的处理逻辑，比如刷新统计数据等
    },
    
    // 打开线路管理对话框
    openLineManagementDialog() {
      this.$refs.lineManagementDialog.open();
    },
    
    // 处理线路更新完成事件
    handleLinesUpdated() {
      // 线路更新后刷新管理员设置以获取最新的线路列表
      if (this.isAdmin) {
        this.fetchAdminSettings();
      }
      this.showMessage('线路配置已更新');
    },
    
    // 处理标签更新完成事件
    handleTagsUpdated() {
      // 可以在这里刷新数据或显示成功提示
      this.showMessage('标签设置已更新');
    },

    // ========== 勋章管理相关方法 ==========
    
    // 获取勋章数据
    async fetchBadgeData() {
      try {
        this.badgesLoading = true
        this.badgesError = null
        
        // 并发获取勋章配置和勋章列表
        const [configResponse, badgesResponse] = await Promise.all([
          getBadgeCenterConfig(),
          adminGetAllBadges()
        ])
        
        this.badgeConfig = configResponse.data
        // 后端返回的是数组,不是 {badges: []}
        this.badges = Array.isArray(badgesResponse.data) ? badgesResponse.data : []
        
        this.badgesLoading = false
      } catch (error) {
        console.error('获取勋章数据失败:', error)
        this.badgesError = error.response?.data?.detail || '获取勋章数据失败'
        this.badgesLoading = false
      }
    },
    
    // 更新勋章配置
    async updateBadgeConfig() {
      try {
        await updateBadgeConfig({
          enabled: this.badgeConfig.enabled,
          message: this.badgeConfig.message
        })
        this.showMessage('勋章配置已更新')
      } catch (error) {
        console.error('更新勋章配置失败:', error)
        this.showMessage('更新勋章配置失败', 'error')
        // 重新获取配置
        await this.fetchBadgeData()
      }
    },
    
    // 打开创建勋章对话框
    openBadgeCreateDialog() {
      this.editingBadge = null
      this.showBadgeEditorDialog = true
    },
    
    // 编辑勋章
    editBadge(badge) {
      this.editingBadge = badge
      this.showBadgeEditorDialog = true
    },
    
    // 删除勋章
    async deleteBadge(badge) {
      const confirmed = await this.showConfirmDialog(
        '确认删除',
        `确定要删除勋章"${badge.name}"吗？\n\n注意：已经兑换的用户勋章不会被删除，但无法再次兑换此勋章。`
      )
      
      if (!confirmed) return
      
      try {
        await adminDeleteBadge(badge.id)
        this.showMessage('勋章已删除')
        // 重新加载勋章列表
        await this.fetchBadgeData()
      } catch (error) {
        console.error('删除勋章失败:', error)
        this.showMessage(error.response?.data?.detail || '删除勋章失败', 'error')
      }
    },
    
    // 勋章保存完成
    async handleBadgeSaved() {
      // 重新加载勋章列表
      await this.fetchBadgeData()
    },
    
    // 显示确认对话框
    showConfirmDialog(title, message) {
      return new Promise((resolve) => {
        if (window.Telegram?.WebApp) {
          window.Telegram.WebApp.showConfirm(message, (confirmed) => {
            resolve(confirmed)
          })
        } else {
          const confirmed = confirm(`${title}\n\n${message}`)
          resolve(confirmed)
        }
      })
    },
    
    // 处理捐赠提交完成事件
    handleDonationSubmitted() {
      this.showMessage('捐赠记录已添加');
    },
    
    // 处理自助捐助登记处理完成事件
    handleRegistrationProcessed(data) {
      const action = data.approved ? '批准' : '拒绝';
      this.showMessage(`已${action}用户 ${data.registration.user_id} 的捐助登记`);
      console.log('登记处理完成:', data);
    },

    // 打开转盘管理
    openWheelManagement() {
      this.showWheelManagement = true;
    },

    // 关闭转盘管理
    closeWheelManagement() {
      this.showWheelManagement = false;
      // 关闭时刷新统计数据
      this.loadWheelStats();
    },

    // 打开 21 点管理
    openBlackjackManagement() {
      this.showBlackjackManagement = true;
    },

    // 关闭 21 点管理
    closeBlackjackManagement() {
      this.showBlackjackManagement = false;
      // 关闭时刷新开关状态，使卡片上的运行中/已停用标记与后端一致；
      // 面板里注入奖池种子会改变余额，故统计也一并重取
      this.loadBlackjackStatus();
      this.loadBlackjackStats();
    },

    // 刷新 21 点管理面板
    async refreshBlackjackPanel() {
      const panel = this.$refs.blackjackAdminPanel;
      if (!panel) {
        this.showMessage('面板尚未就绪', 'warning');
        return;
      }
      await panel.load();
      // 面板自身会在失败时展示错误，此处仅在确实加载成功时才报成功
      if (panel.loadError) {
        this.showMessage(panel.loadError, 'error');
      } else {
        this.showMessage('配置已刷新');
      }
    },

    // 读取 21 点开关状态，用于卡片上的状态标记
    async loadBlackjackStatus() {
      try {
        const response = await getBlackjackConfig()
        this.blackjackEnabled = !!response.data.enabled
      } catch (error) {
        console.error('加载 21 点状态失败:', error);
        this.blackjackEnabled = false
      }
    },

    // 读取 21 点运营统计。仅管理员可调，故只在活动管理 tab 内触发
    async loadBlackjackStats() {
      try {
        const response = await getBlackjackAdminStats()
        this.blackjackStats = response.data
      } catch (error) {
        console.error('加载 21 点统计失败:', error);
      }
    },

    // 打开竞拍管理
    openAuctionManagement() {
      this.showAuctionManagement = true;
      // 打开时加载竞拍数据
      if (this.isAdmin) {
        this.loadAuctionData();
      }
    },

    // 关闭竞拍管理
    closeAuctionManagement() {
      this.showAuctionManagement = false;
    },

    // 刷新转盘统计数据
    async refreshWheelStats() {
      await this.loadWheelStats();
      this.showMessage('统计数据已刷新');
    },

    // 加载转盘统计数据
    async loadWheelStats() {
      try {
        const response = await getWheelStats()
        this.wheelStats = response.data
      } catch (error) {
        console.error('加载转盘统计失败:', error);
        // 使用默认数据
        this.wheelStats = {
          totalSpins: 0,
          activeUsers: 0,
          todaySpins: 0,
          lastWeekSpins: 0,
          totalCreditsChange: 0.0,
          totalCostCredits: 0.0,
          totalInviteCodes: 0
        };
      }
    },

    // 加载竞拍数据
    async loadAuctionData() {
      await Promise.all([
        this.loadAuctionStats(),
        this.loadAuctions()
      ])
    },

    // 加载竞拍统计数据
    async loadAuctionStats() {
      console.log('开始加载竞拍统计数据...')
      try {
        this.auctionStatsLoading = true
        this.auctionStatsError = null
        console.log('调用getAuctionStats API...')
        const response = await getAuctionStats()
        console.log('竞拍统计数据响应:', response.data)
        this.auctionStats = response.data
      } catch (error) {
        console.error('加载竞拍统计失败:', error)
        this.auctionStatsError = error.response?.data?.detail || '加载统计数据失败'
        // 使用默认数据
        this.auctionStats = {
          total_auctions: 0,
          active_auctions: 0,
          total_bids: 0,
          total_value: 0.0
        }
      } finally {
        this.auctionStatsLoading = false
        console.log('竞拍统计数据加载完成:', this.auctionStats)
      }
    },

    // 刷新竞拍统计
    async refreshAuctionStats() {
      await this.loadAuctionStats()
    },

    // 加载竞拍列表
    async loadAuctions() {
      try {
        this.auctionsLoading = true
        this.auctionsError = null
        const response = await getAllAuctions()
        this.auctions = response.data.auctions || []
        this.filterAuctions()
      } catch (error) {
        console.error('加载竞拍列表失败:', error)
        this.auctionsError = error.response?.data?.detail || '加载竞拍列表失败'
        this.auctions = []
        this.filteredAuctions = []
      } finally {
        this.auctionsLoading = false
      }
    },

    // 过滤竞拍活动
    filterAuctions() {
      if (this.auctionStatusFilter === 'all') {
        this.filteredAuctions = [...this.auctions]
      } else {
        this.filteredAuctions = this.auctions.filter(auction => auction.status === this.auctionStatusFilter)
      }
    },

    // 结束过期竞拍
    async finishExpiredAuctionsAction() {
      try {
        this.finishingExpired = true
        await finishExpiredAuctions()
        this.showMessage('已处理过期竞拍活动', 'success')
        await this.loadAuctionData()
      } catch (error) {
        console.error('结束过期竞拍失败:', error)
        this.showMessage('处理过期竞拍失败: ' + (error.response?.data?.detail || error.message), 'error')
      } finally {
        this.finishingExpired = false
      }
    },

    // 查看竞拍详情
    async viewAuctionDetails(auction) {
      try {
        this.selectedAuction = auction
        // 获取竞拍的出价历史
        const response = await getAuctionBids(auction.id)
        this.auctionBids = response.data.bids || []
        this.showAuctionDetailDialog = true
      } catch (error) {
        console.error('获取竞拍详情失败:', error)
        this.showMessage('获取竞拍详情失败: ' + (error.response?.data?.detail || error.message), 'error')
      }
    },

    // 编辑竞拍
    editAuction(auction) {
      this.selectedAuction = auction
      // 填充编辑表单
      this.editAuctionForm = {
        title: auction.title || '',
        description: auction.description || '',
        starting_price: auction.starting_price,
        duration_hours: 72 // 默认72小时
      }
      this.showEditAuctionDialog = true
    },

    // 保存竞拍编辑
    async saveAuctionEdit() {
      if (!this.selectedAuction) return

      try {
        // 确保描述字段不为空
        const updateData = {
          ...this.editAuctionForm,
          description: this.editAuctionForm.description || '竞拍物品'
        }
        await updateAuction(this.selectedAuction.id, updateData)
        this.showMessage('竞拍更新成功', 'success')
        this.showEditAuctionDialog = false
        this.selectedAuction = null
        await this.loadAuctions()
      } catch (error) {
        console.error('更新竞拍失败:', error)
        this.showMessage('更新竞拍失败: ' + (error.response?.data?.detail || error.message), 'error')
      }
    },

    // 关闭竞拍详情对话框
    closeAuctionDetailDialog() {
      this.showAuctionDetailDialog = false
      this.selectedAuction = null
      this.auctionBids = []
    },

    // 关闭编辑竞拍对话框
    closeEditAuctionDialog() {
      this.showEditAuctionDialog = false
      this.selectedAuction = null
      this.editAuctionForm = {
        title: '',
        description: '',
        starting_price: 100,
        duration_hours: 72
      }
    },

    // 结束竞拍
    async finishAuction(auction) {
      const auctionTitle = auction.title || `ID: ${auction.id}` || '未知竞拍活动'
      if (!confirm(`确定要结束竞拍活动 "${auctionTitle}" 吗？`)) {
        return
      }
      
      try {
        await finishAuction(auction.id)
        this.showMessage('竞拍活动已结束', 'success')
        await this.loadAuctions()
      } catch (error) {
        console.error('结束竞拍失败:', error)
        this.showMessage('结束竞拍失败: ' + (error.response?.data?.detail || error.message), 'error')
      }
    },

    // 删除竞拍
    async deleteAuction(auction) {
      const auctionTitle = auction.title || `ID: ${auction.id}` || '未知竞拍活动'
      if (!confirm(`确定要删除竞拍活动 "${auctionTitle}" 吗？此操作不可撤销！`)) {
        return
      }
      
      try {
        await deleteAuction(auction.id)
        this.showMessage('竞拍活动已删除', 'success')
        await this.loadAuctions()
      } catch (error) {
        console.error('删除竞拍失败:', error)
        this.showMessage('删除竞拍失败: ' + (error.response?.data?.detail || error.message), 'error')
      }
    },

    // 获取竞拍状态颜色
    getAuctionStatusColor(status) {
      const colors = {
        active: 'success',
        ended: 'grey',
        cancelled: 'error'
      }
      return colors[status] || 'primary'
    },

    // 获取竞拍状态文本
    getAuctionStatusText(status) {
      const texts = {
        active: '进行中',
        ended: '已结束',
        cancelled: '已取消'
      }
      return texts[status] || status
    },

    // 获取物品类型图标
    getItemTypeIcon(type) {
      const icons = {
        plex: 'mdi-plex',
        emby: 'mdi-emby',
        credits: 'mdi-coin',
        invite: 'mdi-account-plus'
      }
      return icons[type] || 'mdi-package-variant'
    },

    // 获取物品类型颜色
    getItemTypeColor(type) {
      const colors = {
        plex: 'orange',
        emby: 'green',
        credits: 'amber',
        invite: 'blue'
      }
      return colors[type] || 'grey'
    },

    // 格式化日期时间
    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      try {
        const date = new Date(dateTime)
        return date.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (err) {
        return '格式错误'
      }
    },

    // 获取时间状态
    getTimeStatus(endTime) {
      if (!endTime) return ''
      const now = new Date()
      const end = new Date(endTime)
      if (end < now) {
        return '已过期'
      }
      const diff = end - now
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      if (days > 0) {
        return `剩余 ${days} 天 ${hours} 小时`
      } else {
        return `剩余 ${hours} 小时`
      }
    },

    // 创建新竞拍
    async createNewAuction() {
      // 简单验证
      if (!this.createAuctionForm.item_name || 
          this.createAuctionForm.start_price <= 0 || 
          this.createAuctionForm.duration_hours <= 0) {
        this.showMessage('请填写完整的竞拍信息', 'error')
        return
      }
      
      try {
        // 转换字段名以匹配后端API
        const auctionData = {
          title: this.createAuctionForm.item_name,
          description: this.createAuctionForm.description || '竞拍物品',
          starting_price: this.createAuctionForm.start_price,
          duration_hours: this.createAuctionForm.duration_hours
        }
        
        await createAuction(auctionData)
        this.showMessage('竞拍活动创建成功', 'success')
        this.showCreateAuctionDialog = false
        this.resetCreateAuctionForm()
        await this.loadAuctionData()
      } catch (error) {
        console.error('创建竞拍失败:', error)
        this.showMessage('创建竞拍失败: ' + (error.response?.data?.detail || error.message), 'error')
      }
    },

    // 重置创建竞拍表单
    resetCreateAuctionForm() {
      this.createAuctionForm = {
        item_name: '',
        item_type: 'plex',
        start_price: 100,
        duration_hours: 72,
        description: ''
      }
    },
    
    // Premium 线路流量统计相关方法
    async fetchTrafficStats() {
      try {
        this.trafficStatsLoading = true
        this.trafficStatsError = null
        const response = await getPremiumLineTrafficStats()
        this.trafficStats = response.data || []
        this.trafficStatsLoading = false
      } catch (err) {
        this.trafficStatsError = err.response?.data?.detail || '获取流量统计失败'
        this.trafficStatsLoading = false
        console.error('获取Premium线路流量统计失败:', err)
      }
    },
    
    async fetchTrafficOverview() {
      try {
        this.trafficOverviewLoading = true
        this.trafficOverviewError = null
        const response = await getTrafficOverview()
        this.trafficOverview = response.data || {
          today: { total: 0, emby: 0, plex: 0, lines: [], custom_lines: [] },
          week: { total: 0, emby: 0, plex: 0, lines: [], custom_lines: [] },
          month: { total: 0, emby: 0, plex: 0, lines: [], custom_lines: [] }
        }
        this.trafficOverviewLoading = false
      } catch (err) {
        this.trafficOverviewError = err.response?.data?.detail || '获取流量概览失败'
        this.trafficOverviewLoading = false
        console.error('获取流量概览失败:', err)
      }
    },
    
    // 格式化流量大小
    formatTrafficSize(bytes) {
      return formatTrafficSize(bytes)
    },
    
    // 格式化用户名
    formatUsername(username, maxLength = 15) {
      return formatUsername(username, maxLength)
    },
    
    // 获取用户排名颜色
    getUserRankColor(index) {
      const colors = ['amber', 'grey-lighten-1', 'deep-orange-lighten-1', 'blue-lighten-1', 'green-lighten-1']
      return colors[index] || 'grey-lighten-2'
    },
    
    // 获取用户排名图标
    getUserRankIcon(index) {
      const icons = ['mdi-trophy', 'mdi-medal', 'mdi-podium-bronze', 'mdi-numeric-4-circle', 'mdi-numeric-5-circle']
      return icons[index] || 'mdi-account'
    },
    
    // 显示线路用户详情对话框
    showLineUsersDialog(lineStat) {
      this.selectedLineStat = lineStat
      this.showLineUsersDialogVisible = true
    },
    
    // 关闭线路用户详情对话框
    closeLineUsersDialog() {
      this.showLineUsersDialogVisible = false
      this.selectedLineStat = null
    },
    
    // ========== Crypto 捐赠管理相关方法 ========== //
    
    // 打开 Crypto 捐赠管理对话框
    openCryptoDonationDialog() {
      this.showCryptoDonationDialog = true
      this.fetchCryptoDonationOrders()
    },
    
    // 关闭 Crypto 捐赠管理对话框
    closeCryptoDonationDialog() {
      this.showCryptoDonationDialog = false
      this.cryptoDonationOrders = []
      this.cryptoDonationPage = 1
      this.cryptoDonationStatusFilter = null
    },
    
    // 获取 Crypto 捐赠订单列表
    async fetchCryptoDonationOrders() {
      try {
        this.cryptoDonationLoading = true
        this.cryptoDonationError = null
        
        const params = {
          page: this.cryptoDonationPage,
          per_page: this.cryptoDonationPerPage
        }
        
        if (this.cryptoDonationStatusFilter) {
          params.status_filter = this.cryptoDonationStatusFilter
        }
        
        const response = await getAllCryptoDonationOrdersAdmin(params)
        
        if (response.success) {
          this.cryptoDonationOrders = response.data
          this.cryptoDonationTotal = response.total
        } else {
          throw new Error(response.message || '获取数据失败')
        }
        
        this.cryptoDonationLoading = false
      } catch (err) {
        this.cryptoDonationError = err.response?.data?.detail || err.message || '获取 Crypto 捐赠订单失败'
        this.cryptoDonationLoading = false
        console.error('获取 Crypto 捐赠订单失败:', err)
      }
    },
    
    // 状态筛选变化
    onCryptoDonationStatusFilterChange() {
      this.cryptoDonationPage = 1
      this.fetchCryptoDonationOrders()
    },
    
    // 页面变化
    onCryptoDonationPageChange(page) {
      this.cryptoDonationPage = page
      this.fetchCryptoDonationOrders()
    },
    
    // 获取订单状态信息
    getOrderStatusInfo(status) {
      return this.ORDER_STATUS[status] || { text: '未知', color: 'grey', icon: 'mdi-help' }
    },
    
    // 获取用户名（从用户ID）
    async getUserNameById(userId) {
      // 这里可以添加获取用户名的逻辑，暂时返回用户ID
      return `用户 ${userId}`
    }
  }
}
</script>

<style scoped>
.admin-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  padding-bottom: 80px; /* 为底部导航栏留出空间 */
}

.content-wrapper {
  max-width: 800px;
  margin: 0 auto;
}

.admin-header {
  text-align: center;
  margin-bottom: 40px;
  padding: 30px 20px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
}

.page-subtitle {
  font-size: 16px;
  color: #666;
  margin: 0;
}

/* Premium流量统计网格布局 */
.premium-traffic-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 24px;
  margin-top: 8px;
}

/* Premium线路卡片样式 */
.premium-line-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%);
  border-radius: 20px;
  box-shadow: 0 15px 35px rgba(103, 58, 183, 0.1);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(103, 58, 183, 0.15);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
  min-height: 320px;
}

.premium-line-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 25px 50px rgba(103, 58, 183, 0.2);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 1) 100%);
}

.premium-line-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #673ab7 0%, #9c27b0 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.premium-line-card:hover::before {
  opacity: 1;
}

/* 线路标题栏样式 */
.premium-line-header {
  background: linear-gradient(135deg, rgba(103, 58, 183, 0.08) 0%, rgba(156, 39, 176, 0.08) 100%);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(103, 58, 183, 0.15);
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.line-title-wrapper {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.line-icon {
  margin-right: 12px;
  flex-shrink: 0;
}

.line-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  word-break: break-word;
  line-height: 1.3;
  cursor: help;
}

.status-chip {
  flex-shrink: 0;
  margin-left: 12px;
}

/* 统计区域通用样式 */
.traffic-stats-section,
.ranking-section {
  padding: 20px 24px;
}

.stats-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #333;
}

/* 流量指标样式 */
.traffic-metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.metric-item {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.9) 100%);
  border-radius: 12px;
  padding: 16px 12px;
  text-align: center;
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
}

.metric-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.metric-label {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  color: #666;
  margin-bottom: 8px;
  gap: 4px;
}

.metric-value {
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.2;
}

.metric-value.success { color: #2e7d32; }
.metric-value.info { color: #1976d2; }
.metric-value.warning { color: #f57c00; }

/* 排行榜样式 */
.ranking-section {
  border-top: 1px solid rgba(103, 58, 183, 0.1);
  background: linear-gradient(135deg, rgba(103, 58, 183, 0.02) 0%, rgba(156, 39, 176, 0.02) 100%);
}

.ranking-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 10px;
  border: 1px solid rgba(103, 58, 183, 0.1);
  transition: all 0.2s ease;
  margin-bottom: 8px;
}

.ranking-item:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateX(4px);
  box-shadow: 0 4px 12px rgba(103, 58, 183, 0.15);
}

.user-info {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.rank-badge {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
}

.rank-badge.rank-1 { background: linear-gradient(135deg, #ffd700, #ffed4e); }
.rank-badge.rank-2 { background: linear-gradient(135deg, #c0c0c0, #e5e5e5); }
.rank-badge.rank-3 { background: linear-gradient(135deg, #cd7f32, #deb887); }

.username {
  font-size: 0.85rem;
  font-weight: 500;
  color: #333;
  word-break: break-word;
}

.traffic-amount {
  font-size: 0.8rem;
  font-weight: 600;
  color: #666;
  flex-shrink: 0;
}

/* 查看更多按钮 */
.view-more-section {
  text-align: center;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(103, 58, 183, 0.1);
}

.view-more-btn {
  border-radius: 12px;
  text-transform: none;
  font-weight: 500;
}

/* 无数据状态 */
.no-data-section {
  padding: 40px 24px;
  text-align: center;
}

.no-data-content {
  opacity: 0.6;
}

.no-data-text {
  margin-top: 12px;
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0;
}

/* 响应式适配 */
@media (max-width: 768px) {
  .premium-traffic-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .premium-line-card {
    min-height: 280px;
  }
  
  .premium-line-header {
    padding: 16px 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .status-chip {
    margin-left: 0;
    align-self: flex-end;
  }
  
  .traffic-stats-section,
  .ranking-section {
    padding: 16px 20px;
  }
  
  .traffic-metrics {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .metric-item {
    padding: 12px 10px;
  }
  
  .metric-value {
    font-size: 0.9rem;
  }
  
  .ranking-item {
    padding: 10px 12px;
  }
  
  .username {
    font-size: 0.8rem;
  }
  
  .traffic-amount {
    font-size: 0.75rem;
  }
}

@media (max-width: 480px) {
  .premium-line-header {
    padding: 12px 16px;
  }
  
  .line-title {
    font-size: 1rem;
  }
  
  .traffic-stats-section,
  .ranking-section {
    padding: 12px 16px;
  }
  
  .metric-item {
    padding: 10px 8px;
  }
  
  .metric-label {
    font-size: 0.7rem;
  }
  
  .metric-value {
    font-size: 0.85rem;
  }
}

/* 流量统计卡片样式 - 保留原有的简单卡片样式作为备用 */
.line-traffic-card {
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.08);
  height: 100%;
  overflow: hidden;
}

.line-traffic-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

/* 线路标题样式 */
.line-header {
  display: flex;
  align-items: center;
  padding: 16px 16px 8px 16px;
  min-height: 48px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  background-color: rgba(103, 58, 183, 0.02);
  width: 100%;
  box-sizing: border-box;
}

.line-name {
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.2;
  word-wrap: break-word;
  word-break: break-all;
  overflow-wrap: anywhere;
  hyphens: auto;
  flex: 1;
  color: rgba(0, 0, 0, 0.87);
  max-width: calc(100% - 32px);
  white-space: normal;
  display: block;
  cursor: help;
}

.card-content {
  padding: 16px !important;
}

.traffic-stats .v-chip {
  font-weight: 500;
  font-size: 0.75rem;
  min-width: 60px;
  justify-content: center;
}

.traffic-stats .d-flex {
  min-height: 28px;
}

.top-users-list {
  max-height: 120px;
  overflow-y: auto;
}

.user-ranking-list {
  max-height: 400px;
  overflow-y: auto;
}

.user-ranking-list .border-b {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.rank-badge {
  flex-shrink: 0;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .line-traffic-card {
    margin-bottom: 16px;
  }
  
  .line-header {
    padding: 12px 12px 6px 12px;
    min-height: 40px;
  }
  
  .line-name {
    font-size: 0.9rem;
  }
  
  .card-content {
    padding: 12px !important;
  }
  
  .traffic-stats .v-chip {
    font-size: 0.7rem;
  }
  
  .top-users-list {
    max-height: 100px;
  }
}

.admin-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
}

.admin-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 30px 24px;
  text-align: center;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.admin-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
}

/* 美化后的管理卡片样式 */
.admin-card-enhanced {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 30px 24px;
  text-align: center;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
}

.admin-card-enhanced:hover {
  transform: translateY(-8px);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
  background: rgba(255, 255, 255, 0.98);
}

/* 卡片标题样式 */
.admin-card-enhanced .v-card-title {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  backdrop-filter: blur(10px);
  border-radius: 16px 16px 0 0;
  border-bottom: 1px solid rgba(102, 126, 234, 0.2);
  font-weight: 600;
  color: #333;
  padding: 16px 24px;
  margin: -30px -24px 20px;
}

.card-icon {
  margin-bottom: 16px;
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.card-description {
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.5;
}

.coming-soon {
  text-align: center;
  padding: 40px 20px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 16px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
}

.coming-soon-text {
  font-size: 16px;
  color: #666;
  margin-top: 16px;
  margin-bottom: 0;
}

/* 管理控制面板样式 */
.d-flex {
  align-items: center;
}

.d-flex.justify-space-between {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.mr-2 {
  margin-right: 8px;
}

.ml-2 {
  margin-left: 8px;
}

/* 确保卡片文本左对齐 */
.admin-card .v-card-text {
  text-align: left;
}

/* Tab 样式优化 */
/* 标签页容器样式 */
.management-tabs-container {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  margin-bottom: 24px;
  padding: 12px 20px;
  overflow: visible; /* 确保内容不被裁剪 */
}

/* 标签页样式 */
.management-tabs {
  background: transparent !important;
  border-radius: 16px;
  margin-bottom: 0;
  padding: 0;
  overflow: visible !important; /* 确保tab内容不被裁剪 */
  min-width: 100%; /* 确保有足够宽度 */
}

.tab-item {
  font-weight: 600;
  transition: all 0.3s ease;
  border-radius: 12px;
  margin: 0 4px;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-height: 48px;
  text-align: center !important;
  flex-direction: row !important; /* 改为水平排列 */
  gap: 6px !important; /* 添加图标和文字之间的间距 */
  padding: 8px 12px !important; /* 增加内边距确保文字有足够空间 */
  white-space: nowrap !important; /* 防止文字换行 */
  min-width: fit-content !important; /* 确保有足够宽度显示完整文字 */
}

.tab-item .v-icon {
  margin-bottom: 0 !important; /* 移除底部边距 */
  margin-right: 4px !important; /* 添加右边距 */
  flex-shrink: 0 !important; /* 防止图标被压缩 */
}

.tab-text {
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  overflow: visible;
}

/* 覆盖Vuetify默认的tab样式 */
:deep(.v-tab) {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  text-align: center !important;
  flex-direction: row !important; /* 改为水平排列 */
  min-height: 48px !important;
  gap: 6px !important;
  padding: 8px 12px !important;
  white-space: nowrap !important;
  min-width: fit-content !important;
  border-radius: 8px;
  margin: 4px;
  transition: all 0.3s ease;
}

:deep(.v-tab .v-btn__content) {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-direction: row !important; /* 改为水平排列 */
  width: 100% !important;
  text-align: center !important;
  gap: 6px !important;
  white-space: nowrap !important;
}

:deep(.v-tab .v-icon) {
  margin-right: 4px !important; /* 右边距用于分隔图标和文字 */
  margin-bottom: 0 !important; /* 移除底部边距 */
  flex-shrink: 0 !important; /* 防止图标被压缩 */
}

:deep(.v-tab--selected) {
  background: none !important;
  box-shadow: none !important;
}

/* 响应式设计 */
@media (max-width: 600px) {
  .tab-item {
    padding: 6px 8px !important;
    margin: 0 2px;
    font-size: 13px;
  }
  
  .tab-text {
    font-size: 13px;
  }
  
  .tab-item .v-icon {
    margin-right: 3px !important;
  }
  
  :deep(.v-tab) {
    padding: 6px 8px !important;
    gap: 4px !important;
  }
  
  :deep(.v-tab .v-btn__content) {
    gap: 4px !important;
  }
}

@media (max-width: 480px) {
  .tab-item {
    padding: 4px 6px !important;
    margin: 0 1px;
    font-size: 12px;
  }
  
  .tab-text {
    font-size: 12px;
  }
  
  .tab-item .v-icon {
    margin-right: 2px !important;
  }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .admin-container {
    padding: 10px;
  }
  
  .admin-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .admin-header {
    padding: 20px 16px;
    margin-bottom: 30px;
  }
  
  .page-title {
    font-size: 24px;
  }
  
  .admin-card {
    padding: 24px 20px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 22px;
  }
  
  .page-subtitle {
    font-size: 14px;
  }
  
  .card-title {
    font-size: 16px;
  }
  
  .card-description {
    font-size: 13px;
  }
  
  /* 小屏幕上确保控制面板布局正确 */
  .d-flex.justify-space-between {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  /* 活动卡片小屏幕优化 */
  .activity-card-enhanced .v-card-title {
    padding: 12px 16px;
    font-size: 16px;
  }
  
  .stat-value {
    font-size: 18px;
  }
  
  .stat-label {
    font-size: 10px;
  }
  
  .activity-stats {
    padding: 12px;
  }
}

/* 活动网格布局 */
.activities-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
}

/* 活动卡片样式 */
.activity-card-enhanced {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;
}

.activity-card-enhanced:hover {
  transform: translateY(-8px);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);
  background: rgba(255, 255, 255, 0.98);
}

.activity-card-enhanced::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.activity-card-enhanced:hover::before {
  opacity: 1;
}

.activity-card-enhanced .v-card-title {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(102, 126, 234, 0.1);
  font-weight: 600;
  color: #333;
  padding: 20px 24px;
}

/* 活动统计样式 */
.activity-stats {
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid rgba(102, 126, 234, 0.1);
}

.stat-item {
  text-align: center;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  line-height: 1;
}

.stat-label {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* 占位卡片样式 */
.activity-placeholder {
  opacity: 0.7;
}

.activity-placeholder .v-card-title {
  background: linear-gradient(135deg, rgba(158, 158, 158, 0.1) 0%, rgba(189, 189, 189, 0.1) 100%);
  border-bottom: 1px solid rgba(158, 158, 158, 0.1);
}

/* 小屏幕适配 */
@media (max-width: 960px) {
  .auction-management {
    padding: 0 8px;
  }
  
  .stat-card {
    padding: 12px;
  }
  
  .stat-number {
    font-size: 24px;
  }
  
  .stat-label {
    font-size: 11px;
  }
}

@media (max-width: 600px) {
  .auction-table :deep(.v-data-table) {
    font-size: 13px;
  }
  
  .stat-card {
    padding: 8px;
  }
  
  .stat-number {
    font-size: 20px;
  }
}

/* 线路流量卡片样式 */
.line-traffic-card {
  transition: all 0.3s ease;
  border-radius: 8px;
  overflow: hidden;
}

.line-traffic-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.line-traffic-card .v-card-title {
  font-size: 14px;
  font-weight: 600;
}

.line-traffic-card .text-h6 {
  font-weight: 700;
  font-size: 18px;
}

/* 捐赠管理按钮组样式 */
.donation-button-group {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  padding: 4px 0;
}

/* 夺宝期数列表操作区（删除 + 状态） */
.treasure-issue-actions {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.treasure-issue-cancel {
  white-space: nowrap;
  cursor: pointer;
}

.treasure-issue-cancel :deep(.v-chip__content) {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  line-height: 1;
}

.treasure-issue-cancel :deep(.v-icon) {
  margin-inline-end: 0;
}

.treasure-issue-status {
  white-space: nowrap;
}

@media (max-width: 420px) {
  .treasure-issue-actions {
    gap: 6px;
  }
}

.donation-btn {
  height: 44px !important;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0.3px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.donation-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2) !important;
}

.donation-btn:active {
  transform: translateY(0);
}

/* 响应式优化 - 小屏幕下按钮堆叠显示 */
@media (max-width: 600px) {
  .donation-button-group {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .donation-btn {
    width: 100%;
    height: 48px !important;
  }
}

/* Crypto 捐赠表格样式 */
.crypto-donation-table {
  border-radius: 8px;
  overflow: hidden;
}

.crypto-donation-table :deep(.v-data-table__wrapper) {
  border-radius: 8px;
}

.crypto-donation-table :deep(.v-data-table-header__content) {
  font-weight: 600;
  font-size: 13px;
}

.crypto-donation-table :deep(.v-data-table__td) {
  font-size: 13px;
}

/* 响应式表格优化 */
@media (max-width: 960px) {
  .crypto-donation-table :deep(.v-data-table__wrapper) {
    overflow-x: auto;
  }
}

@media (max-width: 600px) {
  .crypto-donation-table :deep(.v-data-table__td),
  .crypto-donation-table :deep(.v-data-table-header__content) {
    font-size: 12px;
    padding: 8px 4px;
  }
}
</style>
